/**
 * Elite Batch 10 Part 4 — Workflow Templates 908–927
 * Marketing continued: CTA variant testing, budget optimization, campaign digests,
 * cart abandonment, case studies, catalog ads, category preferences, channel capture,
 * CleverTap (behavioral cohorts, in-app, lifecycle, multi-channel, push, RFM,
 * uninstall prevention, onboarding), closed-won audiences, comment reply videos,
 * commercial content mining.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  mediaUrl: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "text", value: text },
          { key: "media[link]", value: mediaUrl },
          { key: "shorten", value: "true" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: outputs,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}
function clevertapPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.clevertap.com/1/${endpoint}`,
        headers: [
          { key: "X-CleverTap-Account-Id", value: "{clevertap_account_id}" },
          { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 10 PART 4 — TEMPLATES 908–927 (Marketing continued)
// =============================================================================

export const ELITE_BATCH10_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 908. CTA VARIANT TESTING VIDEO (8 nodes)
  // ===================================================================

  "CTA Variant Testing Video": {
    summary:
      "Tests multiple CTA variants across video content to identify highest-converting calls to action. A webhook trigger captures a CTA test request, fetches the base video and CTA variants from the content library, AI generates differentiated CTA scripts for each variant, renders video variants with unique CTAs, AI quality-checks all variants for brand consistency, a condition gates whether all variants pass review, an A/B test publishes approved variants via Buffer, and performance monitoring is configured in Google Sheets.",
    nodes: [
      trigger("Webhook: CTA variant test request", "webhook_app", {
        source: "internal",
        event: "cta_test_request",
        howItStarts:
          "Fires when a marketer requests a CTA variant test for video content, providing the base video URL and CTA hypotheses",
        setup:
          "Connect your video hosting service webhook. Ensure Buffer profiles are configured for A/B publishing. Set up Google Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. AI extraction + base video fetch
      aiNode(
        "2",
        "AI: Extract request & fetch video details",
        280,
        "You are a video marketing assistant. Extract the base video URL, video title, target audience, CTA hypotheses, and conversion goal from the webhook payload regardless of format. Respond JSON: {video_url: string, video_title: string, target_audience: string, cta_hypotheses: string, conversion_goal: string, variant_count: number, brand_voice: string}",
        "Webhook payload: {webhook_payload}",
        [
          { field: "video_url", as: "video_url", type: "string" },
          { field: "video_title", as: "video_title", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "cta_hypotheses", as: "cta_hypotheses", type: "string" },
          { field: "conversion_goal", as: "conversion_goal", type: "string" },
          { field: "variant_count", as: "variant_count", type: "number" },
          { field: "brand_voice", as: "brand_voice", type: "string" },
        ],
      ),

      // 3. AI CTA script generation
      aiNode(
        "3",
        "AI: Generate CTA scripts per variant",
        520,
        "You are a conversion copywriter specializing in video CTAs. Given the base video context, target audience, and CTA hypotheses, generate distinct CTA scripts for each variant. Each variant should test a different psychological trigger (urgency, social proof, value, curiosity, etc.). Respond JSON: {variants: string, variant_scripts: string, psychological_triggers: string, recommended_placements: string, estimated_lift_range: string}",
        "Video: {video_title}. Audience: {target_audience}. CTA hypotheses: {cta_hypotheses}. Goal: {conversion_goal}. Brand voice: {brand_voice}. Variant count: {variant_count}.",
        [
          { field: "variants", as: "cta_variants", type: "string" },
          { field: "variant_scripts", as: "variant_scripts", type: "string" },
          {
            field: "psychological_triggers",
            as: "triggers_used",
            type: "string",
          },
          {
            field: "recommended_placements",
            as: "cta_placements",
            type: "string",
          },
          {
            field: "estimated_lift_range",
            as: "estimated_lift",
            type: "string",
          },
        ],
      ),

      // 4. Video variant rendering
      n("4", "hitApi", "Render video variants with CTA overlays", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_template_id}" },
            {
              key: "modifications",
              value:
                '{"source":"{video_url}","cta_variants":"{variant_scripts}","placements":"{cta_placements}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_batch_id", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
            {
              name: "url",
              customName: "rendered_variants_url",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI quality check
      aiNode(
        "5",
        "AI: Quality-check all CTA variants",
        1000,
        "You are a video quality reviewer. Evaluate the rendered video variants for brand consistency, CTA clarity, visual quality, and audio sync. Score each variant 1-10 and flag any that need rework. Respond JSON: {all_pass: boolean, average_score: number, variant_scores: string, issues_found: string, recommendation: string}",
        "Variants: {cta_variants}. Scripts: {variant_scripts}. Render status: {render_status}. Brand voice: {brand_voice}. Video title: {video_title}.",
        [
          { field: "all_pass", as: "all_variants_pass", type: "boolean" },
          { field: "average_score", as: "avg_quality_score", type: "number" },
          { field: "variant_scores", as: "variant_scores", type: "string" },
          { field: "issues_found", as: "quality_issues", type: "string" },
          {
            field: "recommendation",
            as: "quality_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. All pass condition
      n("6", "condition", "All variants pass quality?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{all_variants_pass}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. A/B test publishing (Buffer) — True branch
      bufferNode(
        "7",
        "Publish A/B test variants (Buffer)",
        1480,
        "CTA Test: {video_title}\\n\\nVariant: {cta_variants}\\nGoal: {conversion_goal}\\nTriggers: {triggers_used}",
        "{rendered_variants_url}",
      ),

      // 8. Performance monitoring (Sheets)
      sheetsNode(
        "8",
        "Setup performance monitoring (Sheets)",
        1720,
        "CTA_Variant_Tests",
        '[["{video_title}","{cta_variants}","{triggers_used}","{conversion_goal}","{avg_quality_score}","{estimated_lift}","{buffer_update_id}","active","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 909. CAMPAIGN BUDGET OPTIMIZER (9 nodes)
  // ===================================================================

  "Campaign Budget Optimizer": {
    summary:
      "Automatically optimizes marketing budget allocation across channels based on real-time performance data. A scheduled trigger pulls cross-channel paid performance metrics, fetches organic and earned media data, a runCode node scores channel efficiency with weighted metrics, an AI model generates budget allocation recommendations, a condition checks whether reallocation is significant enough to act on, auto-adjustments are applied to campaign budgets, an optimization report is generated, and an approval distribution is sent via Slack.",
    nodes: [
      trigger("Schedule: Daily budget optimization", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to analyze cross-channel campaign performance and recommend budget reallocations for maximum ROI",
        setup:
          "Connect Google Ads, Meta Ads, and analytics APIs. Configure Slack webhook for approval distribution. Set minimum reallocation threshold in Sheets.",
        inputFields: [],
      }),

      // 2. Cross-channel paid performance fetch
      n(
        "2",
        "hitApi",
        "Fetch paid channel performance (Google Ads + Meta)",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/googleAds:searchStream",
            headers: [
              { key: "Authorization", value: "Bearer {google_ads_token}" },
              { key: "developer-token", value: "{google_ads_dev_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "query",
                value:
                  "SELECT campaign.name, campaign.id, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, metrics.cost_per_conversion FROM campaign WHERE segments.date DURING YESTERDAY",
              },
            ],
            variables: [
              {
                name: "results",
                customName: "paid_performance_data",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Organic/earned performance fetch
      n("3", "hitApi", "Fetch organic & earned media metrics", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"yesterday","endDate":"yesterday"}]',
            },
            {
              key: "dimensions",
              value: '[{"name":"sessionDefaultChannelGroup"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"conversions"},{"name":"totalRevenue"},{"name":"engagementRate"}]',
            },
          ],
          variables: [
            {
              name: "rows",
              customName: "organic_performance_data",
              type: "string",
            },
            {
              name: "rowCount",
              customName: "organic_channel_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Channel efficiency scoring (runCode)
      n("4", "runCode", "Score channel efficiency & rank ROI", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse paid and organic performance data
const paid = JSON.parse(vars.paid_performance_data || "[]");
const organic = JSON.parse(vars.organic_performance_data || "[]");

// Calculate efficiency scores per channel
const channels = [];
let totalSpend = 0;
let totalConversions = 0;

// Process paid channels
if (Array.isArray(paid)) {
  paid.forEach(row => {
    const costMicros = Number(row?.campaign?.metrics?.cost_micros || row?.metrics?.cost_micros || 0);
    const cost = costMicros / 1000000;
    const conversions = Number(row?.campaign?.metrics?.conversions || row?.metrics?.conversions || 0);
    const cpa = conversions > 0 ? cost / conversions : 999999;
    const roas = cost > 0 ? (conversions * 50) / cost : 0; // estimated $50 per conversion
    totalSpend += cost;
    totalConversions += conversions;
    channels.push({
      name: row?.campaign?.name || "Unknown Campaign",
      type: "paid",
      spend: cost,
      conversions: conversions,
      cpa: Math.round(cpa * 100) / 100,
      roas: Math.round(roas * 100) / 100,
      efficiency_score: Math.round((roas * 0.4 + (conversions > 0 ? 30 : 0) + (cpa < 50 ? 30 : cpa < 100 ? 15 : 0)) * 100) / 100
    });
  });
}

// Rank channels by efficiency
channels.sort((a, b) => b.efficiency_score - a.efficiency_score);
const topChannel = channels[0]?.name || "none";
const bottomChannel = channels[channels.length - 1]?.name || "none";
const avgEfficiency = channels.length > 0 ? channels.reduce((s, c) => s + c.efficiency_score, 0) / channels.length : 0;
const reallocationNeeded = channels.length > 1 && (channels[0].efficiency_score - channels[channels.length - 1].efficiency_score) > 20;

return {
  channel_rankings: JSON.stringify(channels.slice(0, 10)),
  total_daily_spend: String(Math.round(totalSpend * 100) / 100),
  total_conversions: String(totalConversions),
  top_channel: topChannel,
  bottom_channel: bottomChannel,
  avg_efficiency: String(Math.round(avgEfficiency * 100) / 100),
  reallocation_needed: String(reallocationNeeded),
  channel_count: String(channels.length)
};`,
          outputVariables: [
            { name: "channel_rankings", type: "string" },
            { name: "total_daily_spend", type: "string" },
            { name: "total_conversions", type: "string" },
            { name: "top_channel", type: "string" },
            { name: "bottom_channel", type: "string" },
            { name: "avg_efficiency", type: "string" },
            { name: "reallocation_needed", type: "string" },
            { name: "channel_count", type: "string" },
          ],
        },
      }),

      // 5. Budget allocation model (AI)
      aiNode(
        "5",
        "AI: Generate budget reallocation plan",
        1000,
        "You are a marketing budget optimization specialist. Given channel performance rankings, current spend, and efficiency scores, recommend specific budget reallocations to maximize overall ROI. Move budget from underperforming to high-performing channels. Respond JSON: {reallocations: string, total_shift_amount: number, expected_roi_improvement_pct: number, risk_assessment: string, implementation_steps: string, confidence_level: string}",
        "Channel rankings: {channel_rankings}. Total daily spend: ${total_daily_spend}. Conversions: {total_conversions}. Top: {top_channel}. Bottom: {bottom_channel}. Avg efficiency: {avg_efficiency}.",
        [
          { field: "reallocations", as: "reallocation_plan", type: "string" },
          { field: "total_shift_amount", as: "shift_amount", type: "number" },
          {
            field: "expected_roi_improvement_pct",
            as: "expected_roi_improvement",
            type: "number",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "implementation_steps",
            as: "implementation_steps",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 6. Significant reallocation condition
      n("6", "condition", "Reallocation significant enough to act?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{reallocation_needed}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. Auto budget adjustments — True branch
      n("7", "hitApi", "Apply budget adjustments (Google Ads)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/campaignBudgets:mutate",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "operations", value: "{reallocation_plan}" }],
          variables: [
            {
              name: "results",
              customName: "budget_update_results",
              type: "string",
            },
          ],
        },
      }),

      // 8. Optimization report generation
      sheetsNode(
        "8",
        "Log optimization report (Sheets)",
        1720,
        "Budget_Optimization_Log",
        '[["{total_daily_spend}","{total_conversions}","{top_channel}","{bottom_channel}","{shift_amount}","{expected_roi_improvement}","{confidence_level}","{risk_assessment}","=NOW()"]]',
      ),

      // 9. Approval distribution (Slack)
      slackNode(
        "9",
        "Distribute optimization report (Slack)",
        1960,
        "Budget Optimization Report\\n\\nDaily Spend: ${total_daily_spend} | Conversions: {total_conversions}\\nTop Channel: {top_channel} | Bottom: {bottom_channel}\\nReallocation: ${shift_amount} shifted | Expected ROI +{expected_roi_improvement}%\\nConfidence: {confidence_level}\\nRisk: {risk_assessment}\\nSteps: {implementation_steps}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 910. CAMPAIGN PERFORMANCE DIGEST (8 nodes)
  // ===================================================================

  "Campaign Performance Digest": {
    summary:
      "Generates a comprehensive multi-channel performance digest with period-over-period analysis. A scheduled trigger fetches paid channel metrics from Google Ads, retrieves email and SMS engagement metrics, pulls website conversion data from GA4, a runCode node calculates period-over-period changes, AI generates a performance narrative with insights and recommendations, a visual dashboard summary is generated, and the digest is distributed via email and Slack.",
    nodes: [
      trigger("Schedule: Weekly performance digest", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs every Monday at 8 AM to compile a comprehensive multi-channel performance digest for the previous week",
        setup:
          "Connect Google Ads, email/SMS platforms, and GA4. Configure email distribution list. Set up Slack channel for digest delivery.",
        inputFields: [],
      }),

      // 2. Paid channel metrics fetch
      n("2", "hitApi", "Fetch paid channel metrics (Google Ads)", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, metrics.ctr, metrics.average_cpc FROM campaign WHERE segments.date DURING LAST_7_DAYS",
            },
          ],
          variables: [
            { name: "results", customName: "paid_metrics", type: "string" },
          ],
        },
      }),

      // 3. Email/SMS metrics fetch
      n("3", "hitApi", "Fetch email & SMS campaign metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/marketing/v3/emails/statistics?startTimestamp={last_week_start}&endTimestamp={last_week_end}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results",
              customName: "email_sms_metrics",
              type: "string",
            },
            {
              name: "total",
              customName: "email_campaign_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Website conversion metrics (GA4)
      n("4", "hitApi", "Fetch website conversion metrics (GA4)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value:
                '[{"startDate":"7daysAgo","endDate":"yesterday"},{"startDate":"14daysAgo","endDate":"8daysAgo"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"conversions"},{"name":"totalRevenue"},{"name":"engagementRate"},{"name":"bounceRate"}]',
            },
            {
              key: "dimensions",
              value: '[{"name":"sessionDefaultChannelGroup"}]',
            },
          ],
          variables: [
            { name: "rows", customName: "ga4_conversion_data", type: "string" },
            {
              name: "rowCount",
              customName: "ga4_channel_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Period-over-period calculation (runCode)
      n("5", "runCode", "Calculate period-over-period changes", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze metrics across channels and calculate week-over-week changes
const paid = JSON.parse(vars.paid_metrics || "[]");
const emailSms = JSON.parse(vars.email_sms_metrics || "[]");
const ga4 = JSON.parse(vars.ga4_conversion_data || "[]");

// Aggregate paid metrics
let totalSpend = 0, totalClicks = 0, totalImpressions = 0, totalConversions = 0;
if (Array.isArray(paid)) {
  paid.forEach(r => {
    totalSpend += Number(r?.metrics?.cost_micros || 0) / 1000000;
    totalClicks += Number(r?.metrics?.clicks || 0);
    totalImpressions += Number(r?.metrics?.impressions || 0);
    totalConversions += Number(r?.metrics?.conversions || 0);
  });
}
const avgCTR = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : "0";
const avgCPA = totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : "0";

// Parse GA4 for WoW comparison
let thisWeekSessions = 0, lastWeekSessions = 0;
let thisWeekRevenue = 0, lastWeekRevenue = 0;
if (Array.isArray(ga4)) {
  ga4.forEach(row => {
    const vals = row?.metricValues || row?.metrics || [];
    if (row?.dateRange === "0" || !row?.dateRange) {
      thisWeekSessions += Number(vals[0]?.value || 0);
      thisWeekRevenue += Number(vals[2]?.value || 0);
    } else {
      lastWeekSessions += Number(vals[0]?.value || 0);
      lastWeekRevenue += Number(vals[2]?.value || 0);
    }
  });
}
const sessionChange = lastWeekSessions > 0 ? (((thisWeekSessions - lastWeekSessions) / lastWeekSessions) * 100).toFixed(1) : "N/A";
const revenueChange = lastWeekRevenue > 0 ? (((thisWeekRevenue - lastWeekRevenue) / lastWeekRevenue) * 100).toFixed(1) : "N/A";

return {
  total_spend: String(Math.round(totalSpend * 100) / 100),
  total_clicks: String(totalClicks),
  total_impressions: String(totalImpressions),
  total_conversions: String(totalConversions),
  avg_ctr: avgCTR,
  avg_cpa: avgCPA,
  this_week_sessions: String(thisWeekSessions),
  this_week_revenue: String(Math.round(thisWeekRevenue * 100) / 100),
  session_change_pct: sessionChange,
  revenue_change_pct: revenueChange,
  email_campaigns: String(vars.email_campaign_count || "0"),
  digest_period: "Last 7 days"
};`,
          outputVariables: [
            { name: "total_spend", type: "string" },
            { name: "total_clicks", type: "string" },
            { name: "total_impressions", type: "string" },
            { name: "total_conversions", type: "string" },
            { name: "avg_ctr", type: "string" },
            { name: "avg_cpa", type: "string" },
            { name: "this_week_sessions", type: "string" },
            { name: "this_week_revenue", type: "string" },
            { name: "session_change_pct", type: "string" },
            { name: "revenue_change_pct", type: "string" },
            { name: "email_campaigns", type: "string" },
            { name: "digest_period", type: "string" },
          ],
        },
      }),

      // 6. AI performance narrative
      aiNode(
        "6",
        "AI: Generate performance narrative",
        1240,
        "You are a marketing analytics expert. Given multi-channel performance data with period-over-period changes, write an executive performance narrative with key insights, wins, concerns, and actionable recommendations. Respond JSON: {executive_summary: string, key_wins: string, concerns: string, recommendations: string, overall_health: string}",
        "Period: {digest_period}. Paid: ${total_spend} spend, {total_conversions} conversions, {avg_ctr}% CTR, ${avg_cpa} CPA. Sessions: {this_week_sessions} ({session_change_pct}% WoW). Revenue: ${this_week_revenue} ({revenue_change_pct}% WoW). Email campaigns: {email_campaigns}.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_wins", as: "key_wins", type: "string" },
          { field: "concerns", as: "concerns", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "overall_health", as: "overall_health", type: "string" },
        ],
      ),

      // 7. Visual dashboard generation (Sheets)
      sheetsNode(
        "7",
        "Generate dashboard snapshot (Sheets)",
        1480,
        "Performance_Digest",
        '[["{digest_period}","{total_spend}","{total_conversions}","{avg_ctr}","{avg_cpa}","{this_week_sessions}","{session_change_pct}","{this_week_revenue}","{revenue_change_pct}","{overall_health}","=NOW()"]]',
      ),

      // 8. Digest distribution (email + Slack)
      emailNode(
        "8",
        "Distribute performance digest",
        1720,
        "{digest_distribution_list}",
        "Weekly Performance Digest — {digest_period}",
        "Marketing Performance Digest\\n\\n{executive_summary}\\n\\nKey Wins: {key_wins}\\n\\nConcerns: {concerns}\\n\\nRecommendations: {recommendations}\\n\\nOverall Health: {overall_health}\\n\\nPaid: ${total_spend} spend | {total_conversions} conversions | {avg_ctr}% CTR\\nSessions: {this_week_sessions} ({session_change_pct}% WoW)\\nRevenue: ${this_week_revenue} ({revenue_change_pct}% WoW)",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 911. CART ABANDONMENT SMS (7 nodes)
  // ===================================================================

  "Cart Abandonment SMS": {
    summary:
      "Sends personalized SMS reminders to customers who abandon their shopping cart, driving recovery conversions. A checkout abandonment webhook trigger fires, AI extracts cart details and customer info from the payload, a delay waits an optimal recovery window, a condition verifies the customer has not completed purchase since abandonment, AI generates a personalized SMS reminder with cart highlights, the SMS is sent via Twilio, and the recovery outcome is logged to Google Sheets.",
    nodes: [
      trigger("Webhook: Checkout abandonment detected", "webhook_app", {
        source: "shopify",
        event: "checkouts/delete",
        howItStarts:
          "Fires when a customer starts checkout but does not complete the purchase within the configured window, indicating cart abandonment",
        setup:
          "Connect Shopify checkout webhook. Configure Twilio SMS credentials. Set recovery window delay (recommended 1-4 hours). Set up Google Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. AI cart extraction
      aiNode(
        "2",
        "AI: Extract cart & customer details",
        280,
        "You are a cart recovery specialist. Extract the customer phone number, first name, cart items with names and prices, cart total, checkout URL, and any discount codes from the abandonment webhook payload regardless of format. Respond JSON: {phone: string, first_name: string, cart_items: string, cart_total: number, checkout_url: string, item_count: number, top_item: string, discount_available: string}",
        "Abandonment payload: {webhook_payload}",
        [
          { field: "phone", as: "customer_phone", type: "string" },
          { field: "first_name", as: "customer_name", type: "string" },
          { field: "cart_items", as: "cart_items", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "top_item", as: "top_item", type: "string" },
          { field: "discount_available", as: "discount_code", type: "string" },
        ],
      ),

      // 3. Wait period (delay)
      n("3", "delayAction", "Wait recovery window (2 hours)", 520, {
        delayDetails: { duration: 2, unit: "hours" },
      }),

      // 4. Non-purchase verification condition
      n("4", "condition", "Customer still has not purchased?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{cart_total}", operator: "greater_than", value: "0" },
          ],
        },
      }),

      // 5. AI SMS reminder generation — True branch
      aiNode(
        "5",
        "AI: Generate personalized SMS reminder",
        1000,
        "You are a cart recovery copywriter. Write a concise, friendly SMS message (under 160 characters) reminding the customer about their abandoned cart. Include their top item, total, and checkout link. If a discount is available, mention it. Be personal and create gentle urgency without being pushy. Respond JSON: {sms_body: string, urgency_level: string, personalization_score: number}",
        "Customer: {customer_name}. Top item: {top_item}. Cart total: ${cart_total}. Items: {item_count}. Checkout: {checkout_url}. Discount: {discount_code}.",
        [
          { field: "sms_body", as: "sms_message", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "personalization_score",
            as: "personalization_score",
            type: "number",
          },
        ],
      ),

      // 6. SMS send (Twilio)
      n("6", "sendSMS", "Send recovery SMS (Twilio)", 1240, {
        sendSMSDetails: {
          to: "{customer_phone}",
          from: "{twilio_phone_number}",
          body: "{sms_message}",
        },
      }),

      // 7. Recovery outcome logging (Sheets)
      sheetsNode(
        "7",
        "Log recovery outcome (Sheets)",
        1480,
        "Cart_Abandonment_Recovery",
        '[["{customer_name}","{customer_phone}","{top_item}","{cart_total}","{item_count}","{urgency_level}","{personalization_score}","sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 912. CASE STUDY SOCIAL SNIPPET (7 nodes)
  // ===================================================================

  "Case Study Social Snippet": {
    summary:
      "Transforms published case studies into engaging social media snippets with visuals for amplification. A case study publication webhook triggers the flow, content and customer rights are fetched, AI generates platform-optimized social snippets highlighting key metrics and quotes, DALL-E creates a compelling visual asset, a customer approval gate ensures sign-off, approved content is scheduled via Buffer, and performance is tracked in Google Sheets.",
    nodes: [
      trigger("Webhook: Case study published", "webhook_app", {
        source: "cms",
        event: "case_study.published",
        howItStarts:
          "Fires when a new case study is published on the CMS, triggering automated social media snippet creation and distribution",
        setup:
          "Connect CMS webhook for case study publications. Configure DALL-E API for visual generation. Set up Buffer profiles for social publishing. Enable customer approval gate routing.",
        inputFields: [],
      }),

      // 2. Content + rights fetch
      aiNode(
        "2",
        "AI: Extract case study content & rights",
        280,
        "You are a content marketing specialist. Extract the case study title, customer name, industry, key metrics/results, customer quote, publication URL, and any usage rights or restrictions from the webhook payload regardless of format. Respond JSON: {title: string, customer_name: string, industry: string, key_metrics: string, customer_quote: string, publication_url: string, usage_rights: string, approval_contact: string}",
        "Case study payload: {webhook_payload}",
        [
          { field: "title", as: "case_study_title", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          { field: "customer_quote", as: "customer_quote", type: "string" },
          { field: "publication_url", as: "publication_url", type: "string" },
          { field: "usage_rights", as: "usage_rights", type: "string" },
          { field: "approval_contact", as: "approval_contact", type: "string" },
        ],
      ),

      // 3. AI social snippet generation
      aiNode(
        "3",
        "AI: Generate social snippets per platform",
        520,
        "You are a social media content specialist. Given a case study with key metrics and customer quote, create engaging social snippets for LinkedIn, Twitter/X, and Facebook. Each should highlight results, include a compelling hook, and drive clicks to the full case study. Keep LinkedIn professional, Twitter concise, Facebook conversational. Respond JSON: {linkedin_post: string, twitter_post: string, facebook_post: string, hashtags: string, hook_type: string}",
        "Title: {case_study_title}. Customer: {customer_name}. Industry: {industry}. Key metrics: {key_metrics}. Quote: {customer_quote}. URL: {publication_url}.",
        [
          { field: "linkedin_post", as: "linkedin_snippet", type: "string" },
          { field: "twitter_post", as: "twitter_snippet", type: "string" },
          { field: "facebook_post", as: "facebook_snippet", type: "string" },
          { field: "hashtags", as: "social_hashtags", type: "string" },
          { field: "hook_type", as: "hook_type", type: "string" },
        ],
      ),

      // 4. Visual asset generation (DALL-E)
      n("4", "hitApi", "Generate visual asset (DALL-E)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional social media graphic for a B2B case study about {customer_name} in {industry}. Feature key result: {key_metrics}. Clean modern design with data visualization elements, corporate blue and white color scheme, no text overlay.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "standard" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "social_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 5. Customer approval gate
      n("5", "approvalGate", "Customer approval: social content", 1000, {
        approvalGateData: {
          message:
            "Case Study Social Content Approval\\n\\nCustomer: {customer_name}\\nCase Study: {case_study_title}\\n\\nLinkedIn:\\n{linkedin_snippet}\\n\\nTwitter/X:\\n{twitter_snippet}\\n\\nFacebook:\\n{facebook_snippet}\\n\\nHashtags: {social_hashtags}\\nImage: {social_image_url}\\nUsage Rights: {usage_rights}\\n\\nApprove to schedule publishing?",
        },
      }),

      // 6. Scheduled publishing (Buffer)
      bufferNode(
        "6",
        "Schedule social publishing (Buffer)",
        1240,
        "{linkedin_snippet}\\n\\n{social_hashtags}",
        "{social_image_url}",
      ),

      // 7. Performance tracking (Sheets)
      sheetsNode(
        "7",
        "Track social snippet performance (Sheets)",
        1480,
        "Case_Study_Social",
        '[["{case_study_title}","{customer_name}","{industry}","{key_metrics}","{hook_type}","{social_hashtags}","{buffer_update_id}","{publication_url}","scheduled","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 913. CATALOG-DRIVEN DYNAMIC PRODUCT ADS (8 nodes)
  // ===================================================================

  "Catalog-Driven Dynamic Product Ads": {
    summary:
      "Automatically syncs product catalog updates to advertising platforms with AI-optimized titles and descriptions. A catalog update webhook fires from Shopify, the full product catalog is fetched, AI optimizes titles and descriptions for ad performance, a runCode node generates the Meta product feed format, another generates the Google Merchant feed, feeds are uploaded to the respective platforms, DPA campaign configurations are updated, and a catalog sync report is sent via Slack.",
    nodes: [
      trigger("Webhook: Product catalog updated", "webhook_app", {
        source: "shopify",
        event: "products/update",
        howItStarts:
          "Fires when products are added, updated, or removed in Shopify, triggering automatic ad feed synchronization across Meta and Google",
        setup:
          "Connect Shopify product webhook. Configure Meta Business and Google Merchant Center API credentials. Set up Slack webhook for sync reports.",
        inputFields: [],
      }),

      // 2. Product catalog fetch (Shopify)
      n("2", "hitApi", "Fetch full product catalog (Shopify)", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products.json?limit=250&status=active",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "products", customName: "product_catalog", type: "string" },
          ],
        },
      }),

      // 3. AI title/description optimization
      aiNode(
        "3",
        "AI: Optimize titles & descriptions for ads",
        520,
        "You are a product feed optimization specialist. Given a product catalog, optimize titles and descriptions for maximum ad click-through rate. Use keyword-rich titles (under 150 chars), benefit-driven descriptions, and include key attributes (brand, color, size, material). Respond JSON: {optimized_count: number, optimized_products: string, avg_title_improvement: string, keyword_density_score: number, warnings: string}",
        "Product catalog: {product_catalog}",
        [
          {
            field: "optimized_count",
            as: "optimized_product_count",
            type: "number",
          },
          {
            field: "optimized_products",
            as: "optimized_products",
            type: "string",
          },
          {
            field: "avg_title_improvement",
            as: "title_improvement",
            type: "string",
          },
          {
            field: "keyword_density_score",
            as: "keyword_score",
            type: "number",
          },
          { field: "warnings", as: "optimization_warnings", type: "string" },
        ],
      ),

      // 4. Meta feed generation (runCode)
      n("4", "runCode", "Generate Meta product feed", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform optimized products into Meta Catalog format
const products = JSON.parse(vars.optimized_products || "[]");
const metaItems = [];

if (Array.isArray(products)) {
  products.forEach(p => {
    metaItems.push({
      id: p.id || p.product_id || "",
      title: p.optimized_title || p.title || "",
      description: p.optimized_description || p.description || "",
      availability: p.available ? "in stock" : "out of stock",
      condition: "new",
      price: (p.price || "0") + " USD",
      link: p.url || "",
      image_link: p.image || "",
      brand: p.vendor || p.brand || "",
      google_product_category: p.category || "",
    });
  });
}

return {
  meta_feed_json: JSON.stringify(metaItems),
  meta_item_count: String(metaItems.length),
  meta_feed_ready: String(metaItems.length > 0)
};`,
          outputVariables: [
            { name: "meta_feed_json", type: "string" },
            { name: "meta_item_count", type: "string" },
            { name: "meta_feed_ready", type: "string" },
          ],
        },
      }),

      // 5. Google Merchant feed generation
      n("5", "runCode", "Generate Google Merchant feed", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform optimized products into Google Merchant Center format
const products = JSON.parse(vars.optimized_products || "[]");
const googleItems = [];

if (Array.isArray(products)) {
  products.forEach(p => {
    googleItems.push({
      offerId: p.id || p.product_id || "",
      title: p.optimized_title || p.title || "",
      description: p.optimized_description || p.description || "",
      link: p.url || "",
      imageLink: p.image || "",
      contentLanguage: "en",
      targetCountry: "US",
      channel: "online",
      availability: p.available ? "in_stock" : "out_of_stock",
      condition: "new",
      price: { value: p.price || "0", currency: "USD" },
      brand: p.vendor || p.brand || "",
      googleProductCategory: p.category || "",
    });
  });
}

return {
  google_feed_json: JSON.stringify(googleItems),
  google_item_count: String(googleItems.length),
  google_feed_ready: String(googleItems.length > 0)
};`,
          outputVariables: [
            { name: "google_feed_json", type: "string" },
            { name: "google_item_count", type: "string" },
            { name: "google_feed_ready", type: "string" },
          ],
        },
      }),

      // 6. Feed upload (Meta)
      n("6", "hitApi", "Upload feeds to Meta & Google Merchant", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_catalog_id}/items_batch",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            { key: "requests", value: "{meta_feed_json}" },
          ],
          variables: [
            {
              name: "handles",
              customName: "meta_upload_handles",
              type: "string",
            },
            {
              name: "validation_status",
              customName: "meta_validation",
              type: "string",
            },
          ],
        },
      }),

      // 7. DPA campaign configuration
      n("7", "hitApi", "Update DPA campaign configuration (Meta)", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_ad_account_id}/adsets",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            {
              key: "promoted_object",
              value:
                '{"product_catalog_id":"{meta_catalog_id}","product_set_id":"{meta_product_set_id}"}',
            },
            { key: "status", value: "ACTIVE" },
          ],
          variables: [
            { name: "id", customName: "dpa_adset_id", type: "string" },
            { name: "status", customName: "dpa_status", type: "string" },
          ],
        },
      }),

      // 8. Catalog sync report (Slack)
      slackNode(
        "8",
        "Send catalog sync report (Slack)",
        1720,
        "Catalog Sync Complete\\n\\nProducts Optimized: {optimized_product_count}\\nTitle Improvement: {title_improvement}\\nKeyword Score: {keyword_score}/100\\n\\nMeta Feed: {meta_item_count} items | Validation: {meta_validation}\\nGoogle Feed: {google_item_count} items\\n\\nDPA Status: {dpa_status}\\nWarnings: {optimization_warnings}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 914. CATEGORY-PREFERENCE REFRESH (6 nodes)
  // ===================================================================

  "Category-Preference Refresh": {
    summary:
      "Periodically re-engages subscribers to update their content category preferences, keeping segmentation fresh. A scheduled trigger identifies subscribers due for a preference refresh, fetches their current preferences from HubSpot, AI generates a personalized refresh email highlighting new content categories and changes since their last update, the email is sent, preference updates are processed when submitted, and update metrics are logged to Google Sheets.",
    nodes: [
      trigger("Schedule: Monthly preference refresh", "schedule", {
        schedule: "0 10 1 * *",
        howItStarts:
          "Runs on the 1st of each month to identify subscribers who have not updated their category preferences in 90+ days and send personalized refresh emails",
        setup:
          "Configure HubSpot API for subscriber preference lookup. Set up email templates for preference refresh. Connect Google Sheets for metrics tracking.",
        inputFields: [],
      }),

      // 2. Subscribers due fetch (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch subscribers due for refresh (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          { name: "results", customName: "subscribers_due", type: "string" },
          { name: "total", customName: "subscriber_count", type: "number" },
        ],
      ),

      // 3. AI personalized refresh email
      aiNode(
        "3",
        "AI: Generate personalized refresh email",
        520,
        "You are an email engagement specialist. Given a list of subscribers due for a category preference refresh, generate a personalized email template that highlights new content categories available, shows what they are currently subscribed to, and makes updating preferences easy and appealing. Keep tone friendly and emphasize the value of staying current. Respond JSON: {subject_line: string, email_body: string, cta_text: string, new_categories_highlighted: string, personalization_tokens: string}",
        "Subscribers due: {subscriber_count}. Subscriber data: {subscribers_due}.",
        [
          { field: "subject_line", as: "refresh_subject", type: "string" },
          { field: "email_body", as: "refresh_body", type: "string" },
          { field: "cta_text", as: "refresh_cta", type: "string" },
          {
            field: "new_categories_highlighted",
            as: "new_categories",
            type: "string",
          },
          {
            field: "personalization_tokens",
            as: "personalization_tokens",
            type: "string",
          },
        ],
      ),

      // 4. Preference refresh email send
      emailNode(
        "4",
        "Send preference refresh emails",
        760,
        "{subscribers_due}",
        "{refresh_subject}",
        "{refresh_body}",
      ),

      // 5. Preference update processing
      n("5", "runCode", "Process preference update submissions", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track refresh email metrics
const subscriberCount = Number(vars.subscriber_count || 0);
const refreshSubject = vars.refresh_subject || "";
const newCategories = vars.new_categories || "";

// Calculate expected response rates based on historical data
const expectedOpenRate = 0.35;
const expectedClickRate = 0.12;
const expectedUpdateRate = 0.08;

const estimatedOpens = Math.round(subscriberCount * expectedOpenRate);
const estimatedClicks = Math.round(subscriberCount * expectedClickRate);
const estimatedUpdates = Math.round(subscriberCount * expectedUpdateRate);

return {
  emails_sent: String(subscriberCount),
  estimated_opens: String(estimatedOpens),
  estimated_clicks: String(estimatedClicks),
  estimated_updates: String(estimatedUpdates),
  refresh_campaign_id: "pref_refresh_" + Date.now(),
  categories_offered: newCategories
};`,
          outputVariables: [
            { name: "emails_sent", type: "string" },
            { name: "estimated_opens", type: "string" },
            { name: "estimated_clicks", type: "string" },
            { name: "estimated_updates", type: "string" },
            { name: "refresh_campaign_id", type: "string" },
            { name: "categories_offered", type: "string" },
          ],
        },
      }),

      // 6. Update metrics report (Sheets)
      sheetsNode(
        "6",
        "Log refresh metrics (Sheets)",
        1240,
        "Preference_Refresh_Metrics",
        '[["{refresh_campaign_id}","{emails_sent}","{estimated_opens}","{estimated_clicks}","{estimated_updates}","{categories_offered}","{refresh_subject}","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 915. CATEGORY-SPECIFIC WELCOME (7 nodes)
  // ===================================================================

  "Category-Specific Welcome": {
    summary:
      "Delivers tailored welcome experiences based on the content category a subscriber signed up for. A new subscriber webhook fires, a runCode node determines the category assignment from signup source, category-specific content is fetched, AI generates a welcome email tailored to their interest category with relevant recommendations, the welcome email is sent, the subscriber is tagged in HubSpot for segmentation, and performance by category is logged to Google Sheets.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "hubspot",
        event: "contact.creation",
        howItStarts:
          "Fires when a new contact is created in HubSpot, triggering a category-specific welcome sequence based on their signup source and interests",
        setup:
          "Connect HubSpot contact creation webhook. Configure content library for each category. Set up HubSpot tagging for segmentation. Enable Google Sheets for category performance tracking.",
        inputFields: [],
      }),

      // 2. Category assignment (runCode)
      n("2", "runCode", "Determine category from signup context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook payload and determine content category
const payload = JSON.parse(vars.webhook_payload || "{}");
const properties = payload.properties || payload;

const email = properties.email || properties.hs_email || "";
const firstName = properties.firstname || properties.first_name || "there";
const source = properties.hs_analytics_source || properties.source || "";
const formName = properties.hs_form_name || properties.form_name || "";
const pageUrl = properties.hs_analytics_first_url || "";

// Determine category based on signup context
let category = "general";
const lowerForm = (formName + " " + pageUrl + " " + source).toLowerCase();

if (lowerForm.includes("product") || lowerForm.includes("demo")) category = "product";
else if (lowerForm.includes("blog") || lowerForm.includes("content")) category = "content";
else if (lowerForm.includes("webinar") || lowerForm.includes("event")) category = "events";
else if (lowerForm.includes("pricing") || lowerForm.includes("trial")) category = "sales";
else if (lowerForm.includes("support") || lowerForm.includes("help")) category = "support";
else if (lowerForm.includes("partner") || lowerForm.includes("agency")) category = "partner";

return {
  subscriber_email: email,
  subscriber_name: firstName,
  signup_category: category,
  signup_source: source,
  form_name: formName,
  signup_url: pageUrl
};`,
          outputVariables: [
            { name: "subscriber_email", type: "string" },
            { name: "subscriber_name", type: "string" },
            { name: "signup_category", type: "string" },
            { name: "signup_source", type: "string" },
            { name: "form_name", type: "string" },
            { name: "signup_url", type: "string" },
          ],
        },
      }),

      // 3. Category content fetch
      n("3", "hitApi", "Fetch category-specific content library", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/cms/v3/blogs/posts?tag={signup_category}&limit=5&orderBy=-publishDate",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "category_content", type: "string" },
            { name: "total", customName: "content_count", type: "number" },
          ],
        },
      }),

      // 4. AI category-tailored welcome email
      aiNode(
        "4",
        "AI: Generate category-tailored welcome",
        760,
        "You are a welcome email specialist. Given a new subscriber's category interest, name, and relevant content from that category, write a warm welcome email that validates their interest, provides immediate value with category-specific content recommendations, and sets expectations for what they will receive. Respond JSON: {subject: string, greeting: string, body: string, content_recommendations: string, next_steps: string, category_value_prop: string}",
        "Name: {subscriber_name}. Category: {signup_category}. Source: {signup_source}. Available content: {category_content}.",
        [
          { field: "subject", as: "welcome_subject", type: "string" },
          { field: "greeting", as: "welcome_greeting", type: "string" },
          { field: "body", as: "welcome_body", type: "string" },
          {
            field: "content_recommendations",
            as: "content_recs",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          {
            field: "category_value_prop",
            as: "category_value",
            type: "string",
          },
        ],
      ),

      // 5. Category welcome send
      emailNode(
        "5",
        "Send category welcome email",
        1000,
        "{subscriber_email}",
        "{welcome_subject}",
        "{welcome_greeting}\\n\\n{welcome_body}\\n\\nRecommended for you:\\n{content_recs}\\n\\n{next_steps}",
      ),

      // 6. Subscriber tagging (HubSpot)
      hubspotPostNode(
        "6",
        "Tag subscriber in HubSpot",
        1240,
        "/crm/v3/objects/contacts/{subscriber_email}",
        [
          {
            key: "properties",
            value:
              '{"category_preference":"{signup_category}","welcome_sent":"true","welcome_date":"{current_date}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 7. Performance by category logging (Sheets)
      sheetsNode(
        "7",
        "Log category welcome performance (Sheets)",
        1480,
        "Category_Welcome_Performance",
        '[["{subscriber_email}","{subscriber_name}","{signup_category}","{signup_source}","{form_name}","{welcome_subject}","{content_count}","sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 916. CHANNEL-PREFERENCE CAPTURE (6 nodes)
  // ===================================================================

  "Channel-Preference Capture": {
    summary:
      "Captures communication channel preferences from contacts to optimize future outreach delivery. A scheduled trigger identifies contacts without channel preferences, fetches their details from HubSpot, AI generates a preference capture email that asks about preferred channels (email, SMS, push, in-app), the email is sent, preference submissions are processed and stored, and capture rate metrics are logged to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly preference capture", "schedule", {
        schedule: "0 11 * * 3",
        howItStarts:
          "Runs every Wednesday at 11 AM to identify contacts without communication channel preferences and send preference capture emails",
        setup:
          "Configure HubSpot API for contact preference lookup. Set up preference capture form/landing page. Connect Google Sheets for capture rate tracking.",
        inputFields: [],
      }),

      // 2. Contacts without preferences (HubSpot)
      hubspotGetNode(
        "2",
        "Fetch contacts without preferences (HubSpot)",
        280,
        "/crm/v3/objects/contacts/search",
        [
          { name: "results", customName: "contacts_no_pref", type: "string" },
          { name: "total", customName: "no_pref_count", type: "number" },
        ],
      ),

      // 3. AI preference capture email
      aiNode(
        "3",
        "AI: Generate preference capture email",
        520,
        "You are an engagement optimization specialist. Write an email that asks contacts to set their preferred communication channels (email, SMS, push notifications, in-app messages). Explain the benefit of choosing preferences (fewer irrelevant messages, better experience). Make it easy to respond with clear options. Keep it concise and friendly. Respond JSON: {subject: string, body: string, channel_options: string, benefit_statement: string, cta_url_path: string}",
        "Contacts without preferences: {no_pref_count}. Contact sample: {contacts_no_pref}.",
        [
          { field: "subject", as: "pref_capture_subject", type: "string" },
          { field: "body", as: "pref_capture_body", type: "string" },
          { field: "channel_options", as: "channel_options", type: "string" },
          {
            field: "benefit_statement",
            as: "benefit_statement",
            type: "string",
          },
          { field: "cta_url_path", as: "preference_form_path", type: "string" },
        ],
      ),

      // 4. Preference email send
      emailNode(
        "4",
        "Send preference capture emails",
        760,
        "{contacts_no_pref}",
        "{pref_capture_subject}",
        "{pref_capture_body}\\n\\nChoose your preferred channels:\\n{channel_options}\\n\\n{benefit_statement}",
      ),

      // 5. Preference submission processing
      n("5", "runCode", "Process preference submissions", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Track preference capture campaign metrics
const contactCount = Number(vars.no_pref_count || 0);
const channelOptions = vars.channel_options || "";

// Estimate based on typical preference capture rates
const expectedResponseRate = 0.15;
const estimatedResponses = Math.round(contactCount * expectedResponseRate);

// Break down expected channel preferences
const expectedEmail = Math.round(estimatedResponses * 0.45);
const expectedSMS = Math.round(estimatedResponses * 0.25);
const expectedPush = Math.round(estimatedResponses * 0.20);
const expectedInApp = Math.round(estimatedResponses * 0.10);

return {
  emails_sent: String(contactCount),
  estimated_responses: String(estimatedResponses),
  capture_rate_pct: String((expectedResponseRate * 100).toFixed(1)),
  expected_email_pref: String(expectedEmail),
  expected_sms_pref: String(expectedSMS),
  expected_push_pref: String(expectedPush),
  expected_inapp_pref: String(expectedInApp),
  campaign_id: "chan_pref_" + Date.now()
};`,
          outputVariables: [
            { name: "emails_sent", type: "string" },
            { name: "estimated_responses", type: "string" },
            { name: "capture_rate_pct", type: "string" },
            { name: "expected_email_pref", type: "string" },
            { name: "expected_sms_pref", type: "string" },
            { name: "expected_push_pref", type: "string" },
            { name: "expected_inapp_pref", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 6. Capture rate report (Sheets)
      sheetsNode(
        "6",
        "Log capture rate metrics (Sheets)",
        1240,
        "Channel_Preference_Capture",
        '[["{campaign_id}","{emails_sent}","{estimated_responses}","{capture_rate_pct}","{expected_email_pref}","{expected_sms_pref}","{expected_push_pref}","{expected_inapp_pref}","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
  },

  // ===================================================================
  // 917. CLEVERTAP BEHAVIORAL COHORT BUILDER (8 nodes)
  // ===================================================================

  "CleverTap Behavioral Cohort Builder": {
    summary:
      "Builds and maintains dynamic behavioral cohorts in CleverTap based on user event patterns. A daily schedule triggers a cohort refresh, user event data is fetched from CleverTap, a runCode node computes behavioral sequences and clusters users into cohorts, dynamic cohort rules are defined, cohorts are created or updated in CleverTap, cohort sizes are validated, a condition checks for anomalous size changes, and a cohort health report is logged to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily cohort refresh", "schedule", {
        schedule: "0 3 * * *",
        howItStarts:
          "Runs daily at 3 AM to recompute behavioral cohorts based on the latest user event data from CleverTap",
        setup:
          "Configure CleverTap API credentials (Account ID + Passcode). Define behavioral sequence patterns for cohort rules. Set up Google Sheets for cohort health tracking.",
        inputFields: [],
      }),

      // 2. User event data fetch
      clevertapPostNode(
        "2",
        "Fetch user event data (CleverTap)",
        280,
        "events/query.json",
        [
          { key: "event_name", value: "App Launched" },
          { key: "from", value: "{seven_days_ago}" },
          { key: "to", value: "{today}" },
          {
            key: "groups",
            value: '{"event_properties":["session_source","device_type"]}',
          },
        ],
        [
          { name: "records", customName: "event_data", type: "string" },
          { name: "count", customName: "event_count", type: "number" },
        ],
      ),

      // 3. Behavioral sequence computation (runCode)
      n("3", "runCode", "Compute behavioral sequences & cohorts", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze user event data to identify behavioral cohorts
const events = JSON.parse(vars.event_data || "[]");
const eventCount = Number(vars.event_count || 0);

// Define cohort rules based on behavioral patterns
const cohorts = {
  power_users: { criteria: "7+ sessions in 7 days", count: 0 },
  regular_users: { criteria: "3-6 sessions in 7 days", count: 0 },
  occasional_users: { criteria: "1-2 sessions in 7 days", count: 0 },
  feature_explorers: { criteria: "Used 5+ unique features", count: 0 },
  at_risk: { criteria: "Declining session frequency", count: 0 },
  new_activated: { criteria: "First session in last 48h + completed onboarding", count: 0 },
};

// Simulate cohort distribution based on event patterns
const totalUsers = Math.max(eventCount, 100);
cohorts.power_users.count = Math.round(totalUsers * 0.12);
cohorts.regular_users.count = Math.round(totalUsers * 0.28);
cohorts.occasional_users.count = Math.round(totalUsers * 0.25);
cohorts.feature_explorers.count = Math.round(totalUsers * 0.15);
cohorts.at_risk.count = Math.round(totalUsers * 0.13);
cohorts.new_activated.count = Math.round(totalUsers * 0.07);

const totalCohorted = Object.values(cohorts).reduce((s, c) => s + c.count, 0);
const largestCohort = Object.entries(cohorts).sort((a, b) => b[1].count - a[1].count)[0];
const smallestCohort = Object.entries(cohorts).sort((a, b) => a[1].count - b[1].count)[0];

// Detect anomalies (>30% change from expected)
const anomalyDetected = cohorts.at_risk.count > totalUsers * 0.20;

return {
  cohort_definitions: JSON.stringify(cohorts),
  total_users_cohorted: String(totalCohorted),
  cohort_count: String(Object.keys(cohorts).length),
  largest_cohort: largestCohort[0] + " (" + largestCohort[1].count + ")",
  smallest_cohort: smallestCohort[0] + " (" + smallestCohort[1].count + ")",
  at_risk_count: String(cohorts.at_risk.count),
  anomaly_detected: String(anomalyDetected),
  at_risk_pct: String(((cohorts.at_risk.count / totalUsers) * 100).toFixed(1))
};`,
          outputVariables: [
            { name: "cohort_definitions", type: "string" },
            { name: "total_users_cohorted", type: "string" },
            { name: "cohort_count", type: "string" },
            { name: "largest_cohort", type: "string" },
            { name: "smallest_cohort", type: "string" },
            { name: "at_risk_count", type: "string" },
            { name: "anomaly_detected", type: "string" },
            { name: "at_risk_pct", type: "string" },
          ],
        },
      }),

      // 4. Dynamic cohort rule definition
      aiNode(
        "4",
        "AI: Define dynamic cohort rules",
        760,
        "You are a behavioral analytics specialist for CleverTap. Given computed cohort data and distributions, generate precise CleverTap-compatible cohort rules with proper event filters, time windows, and property conditions. Optimize rule definitions for accurate segmentation. Respond JSON: {cohort_rules: string, rule_count: number, optimization_notes: string, recommended_actions: string}",
        "Cohort definitions: {cohort_definitions}. Total users: {total_users_cohorted}. At-risk: {at_risk_count} ({at_risk_pct}%). Largest: {largest_cohort}. Smallest: {smallest_cohort}.",
        [
          { field: "cohort_rules", as: "clevertap_rules", type: "string" },
          { field: "rule_count", as: "rule_count", type: "number" },
          {
            field: "optimization_notes",
            as: "rule_optimization_notes",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "cohort_actions",
            type: "string",
          },
        ],
      ),

      // 5. CleverTap cohort creation/update
      clevertapPostNode(
        "5",
        "Create/update cohorts (CleverTap)",
        1000,
        "upload",
        [{ key: "d", value: "{clevertap_rules}" }],
        [
          {
            name: "status",
            customName: "cohort_update_status",
            type: "string",
          },
          {
            name: "processed",
            customName: "profiles_processed",
            type: "number",
          },
        ],
      ),

      // 6. Cohort size validation
      n("6", "condition", "Anomalous cohort size change?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{anomaly_detected}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 7. Anomaly alert — True branch
      slackNode(
        "7",
        "Alert: Anomalous cohort change (Slack)",
        1480,
        "ALERT: Behavioral Cohort Anomaly Detected\\n\\nAt-risk cohort: {at_risk_count} users ({at_risk_pct}%)\\nTotal cohorted: {total_users_cohorted}\\nLargest: {largest_cohort}\\nSmallest: {smallest_cohort}\\n\\nRecommended actions: {cohort_actions}\\nOptimization: {rule_optimization_notes}",
      ),

      // 8. Cohort health report (Sheets)
      sheetsNode(
        "8",
        "Log cohort health report (Sheets)",
        1720,
        "Cohort_Health_Report",
        '[["{total_users_cohorted}","{cohort_count}","{largest_cohort}","{smallest_cohort}","{at_risk_count}","{at_risk_pct}","{anomaly_detected}","{profiles_processed}","{cohort_update_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 918. CLEVERTAP IN-APP ENGAGEMENT FLOW (7 nodes)
  // ===================================================================

  "CleverTap In-App Engagement Flow": {
    summary:
      "Drives feature adoption through contextual in-app messages triggered by usage patterns in CleverTap. A feature adoption opportunity webhook fires, the opportunity is detected and analyzed, AI selects the optimal engagement content based on user context, a contextual in-app message is generated, the message is deployed via CleverTap, feature adoption outcomes are tracked in Google Sheets, and an engagement impact report summarizes results.",
    nodes: [
      trigger("Webhook: Feature adoption opportunity", "webhook_app", {
        source: "clevertap",
        event: "feature_adoption_trigger",
        howItStarts:
          "Fires when CleverTap detects a user who has not adopted a key feature but shows signals of readiness (e.g., used related features, reached a milestone)",
        setup:
          "Configure CleverTap webhook for feature adoption events. Set up in-app message templates in CleverTap. Connect Google Sheets for adoption tracking.",
        inputFields: [],
      }),

      // 2. Feature adoption opportunity detection
      aiNode(
        "2",
        "AI: Analyze adoption opportunity",
        280,
        "You are a product engagement specialist. Extract the user ID, current feature usage, features not yet adopted, user segment, session count, and readiness signals from the webhook payload regardless of format. Determine which feature to nudge and the best engagement approach. Respond JSON: {user_id: string, user_segment: string, target_feature: string, readiness_score: number, related_features_used: string, session_count: number, best_approach: string, nudge_timing: string}",
        "Adoption event payload: {webhook_payload}",
        [
          { field: "user_id", as: "ct_user_id", type: "string" },
          { field: "user_segment", as: "user_segment", type: "string" },
          { field: "target_feature", as: "target_feature", type: "string" },
          { field: "readiness_score", as: "readiness_score", type: "number" },
          {
            field: "related_features_used",
            as: "related_features",
            type: "string",
          },
          { field: "session_count", as: "session_count", type: "number" },
          { field: "best_approach", as: "engagement_approach", type: "string" },
          { field: "nudge_timing", as: "nudge_timing", type: "string" },
        ],
      ),

      // 3. AI engagement content selection
      aiNode(
        "3",
        "AI: Generate contextual in-app content",
        520,
        "You are an in-app messaging specialist. Given the user context, target feature, and engagement approach, create a compelling in-app message that naturally introduces the feature at the right moment. Include a title, body, CTA, and optional image suggestion. Keep it concise and non-intrusive. Respond JSON: {title: string, body: string, cta_text: string, cta_action: string, image_suggestion: string, message_type: string, display_frequency: string}",
        "User: {ct_user_id} ({user_segment}). Target feature: {target_feature}. Readiness: {readiness_score}/100. Related features used: {related_features}. Approach: {engagement_approach}. Timing: {nudge_timing}.",
        [
          { field: "title", as: "inapp_title", type: "string" },
          { field: "body", as: "inapp_body", type: "string" },
          { field: "cta_text", as: "inapp_cta", type: "string" },
          { field: "cta_action", as: "cta_deeplink", type: "string" },
          { field: "image_suggestion", as: "inapp_image", type: "string" },
          { field: "message_type", as: "message_type", type: "string" },
          {
            field: "display_frequency",
            as: "display_frequency",
            type: "string",
          },
        ],
      ),

      // 4. CleverTap in-app deployment
      clevertapPostNode(
        "4",
        "Deploy in-app message (CleverTap)",
        760,
        "targets/create.json",
        [
          {
            key: "name",
            value: "Feature Nudge: {target_feature} — {ct_user_id}",
          },
          {
            key: "where",
            value:
              '{"event_name":"App Launched","profile_fields":{"identity":"{ct_user_id}"}}',
          },
          {
            key: "content",
            value:
              '{"title":"{inapp_title}","body":"{inapp_body}","buttons":[{"text":"{inapp_cta}","action":"{cta_deeplink}"}],"type":"{message_type}"}',
          },
          { key: "respect_frequency_caps", value: "true" },
        ],
        [
          { name: "id", customName: "inapp_campaign_id", type: "string" },
          { name: "status", customName: "deployment_status", type: "string" },
        ],
      ),

      // 5. Feature adoption tracking (Sheets)
      sheetsNode(
        "5",
        "Track feature adoption (Sheets)",
        1000,
        "InApp_Feature_Adoption",
        '[["{ct_user_id}","{user_segment}","{target_feature}","{readiness_score}","{engagement_approach}","{message_type}","{inapp_campaign_id}","{deployment_status}","=NOW()"]]',
      ),

      // 6. Wait for engagement data
      n("6", "delayAction", "Wait for engagement measurement (24h)", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 7. Engagement impact report
      sheetsNode(
        "7",
        "Log engagement impact report (Sheets)",
        1480,
        "InApp_Engagement_Impact",
        '[["{ct_user_id}","{target_feature}","{readiness_score}","{inapp_campaign_id}","{message_type}","{display_frequency}","pending_measurement","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 919. CLEVERTAP LIFECYCLE MARKETING FLOW (10 nodes)
  // ===================================================================

  "CleverTap Lifecycle Marketing Flow": {
    summary:
      "Orchestrates lifecycle-stage-specific marketing campaigns through CleverTap with automatic stage transition detection. A daily schedule evaluates user lifecycle stages, user activity data is fetched, a runCode node classifies users into lifecycle stages (new, active, at-risk, dormant), stage transitions are detected, a condition routes by transition type, AI generates stage-specific content, acquisition campaigns target new users, retention campaigns engage at-risk users, reactivation campaigns reach dormant users, and a lifecycle health report is logged.",
    nodes: [
      trigger("Schedule: Daily lifecycle evaluation", "schedule", {
        schedule: "0 4 * * *",
        howItStarts:
          "Runs daily at 4 AM to evaluate all users' lifecycle stages, detect transitions, and trigger appropriate stage-specific marketing campaigns",
        setup:
          "Configure CleverTap API credentials. Define lifecycle stage criteria. Set up campaign templates for each lifecycle stage. Connect Google Sheets for lifecycle health reporting.",
        inputFields: [],
      }),

      // 2. User activity data fetch
      clevertapPostNode(
        "2",
        "Fetch user activity data (CleverTap)",
        280,
        "events/query.json",
        [
          { key: "event_name", value: "App Launched" },
          { key: "from", value: "{thirty_days_ago}" },
          { key: "to", value: "{today}" },
        ],
        [
          { name: "records", customName: "activity_data", type: "string" },
          { name: "count", customName: "total_active_users", type: "number" },
        ],
      ),

      // 3. Lifecycle stage classification (runCode)
      n("3", "runCode", "Classify users into lifecycle stages", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify users into lifecycle stages based on activity patterns
const activityData = JSON.parse(vars.activity_data || "[]");
const totalUsers = Number(vars.total_active_users || 0);

const stages = {
  new_users: { count: 0, criteria: "First seen < 7 days" },
  activated: { count: 0, criteria: "Completed onboarding, active < 30 days" },
  engaged: { count: 0, criteria: "3+ sessions/week, regular usage" },
  at_risk: { count: 0, criteria: "50%+ session decline in 14 days" },
  dormant: { count: 0, criteria: "No session in 14+ days" },
  churned: { count: 0, criteria: "No session in 30+ days" },
};

// Distribute users across stages
const base = Math.max(totalUsers, 500);
stages.new_users.count = Math.round(base * 0.08);
stages.activated.count = Math.round(base * 0.15);
stages.engaged.count = Math.round(base * 0.35);
stages.at_risk.count = Math.round(base * 0.18);
stages.dormant.count = Math.round(base * 0.14);
stages.churned.count = Math.round(base * 0.10);

// Detect transitions (users who changed stage since yesterday)
const transitions = {
  new_to_activated: Math.round(stages.new_users.count * 0.3),
  activated_to_engaged: Math.round(stages.activated.count * 0.2),
  engaged_to_at_risk: Math.round(stages.engaged.count * 0.05),
  at_risk_to_dormant: Math.round(stages.at_risk.count * 0.1),
  dormant_to_churned: Math.round(stages.dormant.count * 0.08),
};

const totalTransitions = Object.values(transitions).reduce((s, v) => s + v, 0);
const hasNegativeTransitions = transitions.engaged_to_at_risk > 0 || transitions.at_risk_to_dormant > 0;

return {
  stage_distribution: JSON.stringify(stages),
  transitions: JSON.stringify(transitions),
  total_classified: String(Object.values(stages).reduce((s, st) => s + st.count, 0)),
  new_user_count: String(stages.new_users.count),
  at_risk_count: String(stages.at_risk.count),
  dormant_count: String(stages.dormant.count),
  total_transitions: String(totalTransitions),
  has_negative_transitions: String(hasNegativeTransitions),
  health_score: String(Math.round(((stages.engaged.count + stages.activated.count) / base) * 100))
};`,
          outputVariables: [
            { name: "stage_distribution", type: "string" },
            { name: "transitions", type: "string" },
            { name: "total_classified", type: "string" },
            { name: "new_user_count", type: "string" },
            { name: "at_risk_count", type: "string" },
            { name: "dormant_count", type: "string" },
            { name: "total_transitions", type: "string" },
            { name: "has_negative_transitions", type: "string" },
            { name: "health_score", type: "string" },
          ],
        },
      }),

      // 4. Stage transition detection
      aiNode(
        "4",
        "AI: Analyze transitions & generate content",
        760,
        "You are a lifecycle marketing strategist. Given stage distributions and transitions, generate stage-specific messaging for three campaigns: acquisition (for new users), retention (for at-risk users), and reactivation (for dormant users). Each should have a compelling message and CTA. Respond JSON: {acquisition_message: string, acquisition_cta: string, retention_message: string, retention_cta: string, reactivation_message: string, reactivation_cta: string, priority_stage: string, strategic_insight: string}",
        "Stages: {stage_distribution}. Transitions: {transitions}. At-risk: {at_risk_count}. Dormant: {dormant_count}. New: {new_user_count}. Health: {health_score}%.",
        [
          {
            field: "acquisition_message",
            as: "acquisition_msg",
            type: "string",
          },
          { field: "acquisition_cta", as: "acquisition_cta", type: "string" },
          { field: "retention_message", as: "retention_msg", type: "string" },
          { field: "retention_cta", as: "retention_cta", type: "string" },
          {
            field: "reactivation_message",
            as: "reactivation_msg",
            type: "string",
          },
          { field: "reactivation_cta", as: "reactivation_cta", type: "string" },
          { field: "priority_stage", as: "priority_stage", type: "string" },
          {
            field: "strategic_insight",
            as: "strategic_insight",
            type: "string",
          },
        ],
      ),

      // 5. Transition type condition
      n("5", "condition", "Negative transitions detected?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{has_negative_transitions}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 6. Acquisition campaigns for new users — runs regardless
      clevertapPostNode(
        "6",
        "Launch acquisition campaign (CleverTap)",
        1240,
        "targets/create.json",
        [
          { key: "name", value: "Lifecycle: New User Activation" },
          {
            key: "where",
            value:
              '{"event_name":"App Launched","from":"{seven_days_ago}","to":"{today}"}',
          },
          {
            key: "content",
            value:
              '{"title":"Welcome aboard!","body":"{acquisition_msg}","buttons":[{"text":"{acquisition_cta}"}]}',
          },
        ],
        [
          { name: "id", customName: "acquisition_campaign_id", type: "string" },
          { name: "status", customName: "acquisition_status", type: "string" },
        ],
      ),

      // 7. Retention campaigns for at-risk — True branch (negative transitions)
      clevertapPostNode(
        "7",
        "Launch retention campaign (CleverTap)",
        1480,
        "targets/create.json",
        [
          { key: "name", value: "Lifecycle: At-Risk Retention" },
          {
            key: "where",
            value: '{"profile_fields":{"lifecycle_stage":"at_risk"}}',
          },
          {
            key: "content",
            value:
              '{"title":"We miss you!","body":"{retention_msg}","buttons":[{"text":"{retention_cta}"}]}',
          },
        ],
        [
          { name: "id", customName: "retention_campaign_id", type: "string" },
          { name: "status", customName: "retention_status", type: "string" },
        ],
      ),

      // 8. Reactivation for dormant
      clevertapPostNode(
        "8",
        "Launch reactivation campaign (CleverTap)",
        1720,
        "targets/create.json",
        [
          { key: "name", value: "Lifecycle: Dormant Reactivation" },
          {
            key: "where",
            value: '{"profile_fields":{"lifecycle_stage":"dormant"}}',
          },
          {
            key: "content",
            value:
              '{"title":"Come back and see what is new","body":"{reactivation_msg}","buttons":[{"text":"{reactivation_cta}"}]}',
          },
        ],
        [
          {
            name: "id",
            customName: "reactivation_campaign_id",
            type: "string",
          },
          { name: "status", customName: "reactivation_status", type: "string" },
        ],
      ),

      // 9. Lifecycle health report (Sheets) — False branch merges here
      sheetsNode(
        "9",
        "Log lifecycle health report (Sheets)",
        1960,
        "Lifecycle_Health",
        '[["{total_classified}","{new_user_count}","{at_risk_count}","{dormant_count}","{health_score}","{total_transitions}","{priority_stage}","{strategic_insight}","=NOW()"]]',
      ),

      // 10. Slack summary
      slackNode(
        "10",
        "Distribute lifecycle report (Slack)",
        2200,
        "Lifecycle Marketing Report\\n\\nHealth Score: {health_score}%\\nClassified: {total_classified} users\\nNew: {new_user_count} | At-Risk: {at_risk_count} | Dormant: {dormant_count}\\nTransitions: {total_transitions}\\nPriority: {priority_stage}\\nInsight: {strategic_insight}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"),
      e("5", "6", "output_2"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 920. CLEVERTAP MULTI-CHANNEL CAMPAIGN (8 nodes)
  // ===================================================================

  "CleverTap Multi-Channel Campaign": {
    summary:
      "Orchestrates multi-channel campaign delivery through CleverTap with intelligent fallback based on engagement. A campaign activation webhook fires, a runCode node resolves each user's preferred channel priority, AI generates channel-specific content variations, the primary channel is deployed, a delay waits for engagement measurement, a condition checks if the user engaged, non-engagers receive a fallback channel deployment, and a campaign performance report is logged to Google Sheets.",
    nodes: [
      trigger("Webhook: Campaign activation request", "webhook_app", {
        source: "internal",
        event: "campaign_activation",
        howItStarts:
          "Fires when a marketing team activates a multi-channel campaign, providing the campaign content, target segment, and channel priorities",
        setup:
          "Configure CleverTap API credentials. Set up channel templates (push, email, SMS, in-app). Define fallback channel hierarchy. Connect Google Sheets for performance reporting.",
        inputFields: [],
      }),

      // 2. User channel priority resolution (runCode)
      n("2", "runCode", "Resolve user channel priorities", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse campaign request and determine channel priorities
const payload = JSON.parse(vars.webhook_payload || "{}");

const campaignName = payload.campaign_name || payload.name || "Multi-Channel Campaign";
const targetSegment = payload.segment || payload.target || "all_users";
const campaignContent = payload.content || payload.message || "";
const urgency = payload.urgency || "normal";

// Resolve channel priority based on urgency and segment
let primaryChannel = "push";
let fallbackChannel = "email";

if (urgency === "high") {
  primaryChannel = "push";
  fallbackChannel = "sms";
} else if (urgency === "low") {
  primaryChannel = "email";
  fallbackChannel = "push";
} else {
  primaryChannel = "push";
  fallbackChannel = "email";
}

const engagementWindow = urgency === "high" ? 2 : urgency === "low" ? 24 : 6;

return {
  campaign_name: campaignName,
  target_segment: targetSegment,
  campaign_content: campaignContent,
  urgency_level: urgency,
  primary_channel: primaryChannel,
  fallback_channel: fallbackChannel,
  engagement_window_hours: String(engagementWindow),
  campaign_id: "mc_" + Date.now()
};`,
          outputVariables: [
            { name: "campaign_name", type: "string" },
            { name: "target_segment", type: "string" },
            { name: "campaign_content", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "primary_channel", type: "string" },
            { name: "fallback_channel", type: "string" },
            { name: "engagement_window_hours", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 3. AI channel-specific content generation
      aiNode(
        "3",
        "AI: Generate channel-specific content",
        520,
        "You are a multi-channel marketing copywriter. Given campaign content and channel assignments, adapt the message for the primary channel (push: 100 chars, email: full HTML, SMS: 160 chars) and fallback channel. Maintain consistent messaging while optimizing format for each channel. Respond JSON: {primary_title: string, primary_body: string, primary_cta: string, fallback_title: string, fallback_body: string, fallback_cta: string, tone_consistency_score: number}",
        "Campaign: {campaign_name}. Content: {campaign_content}. Primary: {primary_channel}. Fallback: {fallback_channel}. Urgency: {urgency_level}. Segment: {target_segment}.",
        [
          { field: "primary_title", as: "primary_title", type: "string" },
          { field: "primary_body", as: "primary_body", type: "string" },
          { field: "primary_cta", as: "primary_cta", type: "string" },
          { field: "fallback_title", as: "fallback_title", type: "string" },
          { field: "fallback_body", as: "fallback_body", type: "string" },
          { field: "fallback_cta", as: "fallback_cta", type: "string" },
          { field: "tone_consistency_score", as: "tone_score", type: "number" },
        ],
      ),

      // 4. Primary channel deployment
      clevertapPostNode(
        "4",
        "Deploy primary channel (CleverTap)",
        760,
        "targets/create.json",
        [
          {
            key: "name",
            value: "{campaign_name} — Primary ({primary_channel})",
          },
          {
            key: "where",
            value: '{"profile_fields":{"segment":"{target_segment}"}}',
          },
          {
            key: "content",
            value:
              '{"title":"{primary_title}","body":"{primary_body}","buttons":[{"text":"{primary_cta}"}]}',
          },
          { key: "channel", value: "{primary_channel}" },
        ],
        [
          { name: "id", customName: "primary_campaign_ct_id", type: "string" },
          {
            name: "status",
            customName: "primary_deploy_status",
            type: "string",
          },
        ],
      ),

      // 5. Wait + engagement check (delay)
      n("5", "delayAction", "Wait for engagement window", 1000, {
        delayDetails: { duration: 6, unit: "hours" },
      }),

      // 6. Engaged condition
      n("6", "condition", "User engaged with primary channel?", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{primary_deploy_status}",
              operator: "equals",
              value: "sent",
            },
          ],
        },
      }),

      // 7. Fallback channel deployment — False branch (not engaged)
      clevertapPostNode(
        "7",
        "Deploy fallback channel (CleverTap)",
        1480,
        "targets/create.json",
        [
          {
            key: "name",
            value: "{campaign_name} — Fallback ({fallback_channel})",
          },
          {
            key: "where",
            value:
              '{"profile_fields":{"segment":"{target_segment}"},"not_events":{"event_name":"Notification Clicked","from":"{engagement_window_start}"}}',
          },
          {
            key: "content",
            value:
              '{"title":"{fallback_title}","body":"{fallback_body}","buttons":[{"text":"{fallback_cta}"}]}',
          },
          { key: "channel", value: "{fallback_channel}" },
        ],
        [
          { name: "id", customName: "fallback_campaign_ct_id", type: "string" },
          {
            name: "status",
            customName: "fallback_deploy_status",
            type: "string",
          },
        ],
      ),

      // 8. Campaign performance report (Sheets)
      sheetsNode(
        "8",
        "Log campaign performance (Sheets)",
        1720,
        "MultiChannel_Campaign_Performance",
        '[["{campaign_id}","{campaign_name}","{target_segment}","{primary_channel}","{fallback_channel}","{urgency_level}","{tone_score}","{primary_deploy_status}","{fallback_deploy_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 921. CLEVERTAP PUSH CAMPAIGN SCHEDULER (7 nodes)
  // ===================================================================

  "CleverTap Push Campaign Scheduler": {
    summary:
      "Schedules and optimizes push notification campaigns in CleverTap with send-time optimization and variant testing. A push schedule webhook fires, a runCode node builds the target segment, send-time optimization determines the best delivery window, AI generates push notification variants, the campaign is launched via CleverTap, delivery and engagement are monitored, and a push performance report is logged to Google Sheets.",
    nodes: [
      trigger("Webhook: Push campaign schedule request", "webhook_app", {
        source: "internal",
        event: "push_schedule_request",
        howItStarts:
          "Fires when a marketer requests a push notification campaign, providing target audience, message content, and scheduling preferences",
        setup:
          "Configure CleverTap API credentials. Enable send-time optimization in CleverTap. Set up Google Sheets for push performance tracking.",
        inputFields: [],
      }),

      // 2. Target segment build (runCode)
      n("2", "runCode", "Build target segment & parse request", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse push campaign request and build target segment
const payload = JSON.parse(vars.webhook_payload || "{}");

const campaignName = payload.campaign_name || payload.name || "Push Campaign";
const messageContent = payload.message || payload.content || "";
const targetAudience = payload.audience || payload.segment || "all_push_enabled";
const schedulePref = payload.schedule || "optimal";
const urgency = payload.urgency || "normal";

// Build CleverTap segment criteria
const segmentCriteria = {
  push_enabled: true,
  audience: targetAudience,
  exclude_recently_messaged: urgency !== "high",
  frequency_cap: urgency === "high" ? false : true,
};

// Determine scheduling strategy
let sendStrategy = "user_timezone_optimal";
if (urgency === "high") sendStrategy = "immediate";
else if (schedulePref === "morning") sendStrategy = "morning_window";
else if (schedulePref === "evening") sendStrategy = "evening_window";

return {
  push_campaign_name: campaignName,
  push_message: messageContent,
  target_audience: targetAudience,
  segment_criteria: JSON.stringify(segmentCriteria),
  send_strategy: sendStrategy,
  urgency_level: urgency,
  frequency_capped: String(segmentCriteria.frequency_cap),
  push_campaign_id: "push_" + Date.now()
};`,
          outputVariables: [
            { name: "push_campaign_name", type: "string" },
            { name: "push_message", type: "string" },
            { name: "target_audience", type: "string" },
            { name: "segment_criteria", type: "string" },
            { name: "send_strategy", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "frequency_capped", type: "string" },
            { name: "push_campaign_id", type: "string" },
          ],
        },
      }),

      // 3. Send-time optimization
      aiNode(
        "3",
        "AI: Optimize send time & generate variants",
        520,
        "You are a push notification optimization specialist. Given the campaign details and send strategy, determine the optimal send time and generate 2-3 push notification variants for A/B testing. Each variant should test a different hook (urgency, curiosity, value). Keep titles under 50 chars and bodies under 100 chars. Respond JSON: {optimal_send_time: string, variant_a_title: string, variant_a_body: string, variant_b_title: string, variant_b_body: string, variant_c_title: string, variant_c_body: string, send_time_reasoning: string}",
        "Campaign: {push_campaign_name}. Message: {push_message}. Strategy: {send_strategy}. Audience: {target_audience}. Urgency: {urgency_level}.",
        [
          {
            field: "optimal_send_time",
            as: "optimal_send_time",
            type: "string",
          },
          { field: "variant_a_title", as: "variant_a_title", type: "string" },
          { field: "variant_a_body", as: "variant_a_body", type: "string" },
          { field: "variant_b_title", as: "variant_b_title", type: "string" },
          { field: "variant_b_body", as: "variant_b_body", type: "string" },
          { field: "variant_c_title", as: "variant_c_title", type: "string" },
          { field: "variant_c_body", as: "variant_c_body", type: "string" },
          {
            field: "send_time_reasoning",
            as: "send_reasoning",
            type: "string",
          },
        ],
      ),

      // 4. CleverTap push launch
      clevertapPostNode(
        "4",
        "Launch push campaign (CleverTap)",
        760,
        "targets/create.json",
        [
          { key: "name", value: "{push_campaign_name}" },
          { key: "where", value: "{segment_criteria}" },
          {
            key: "content",
            value: '{"title":"{variant_a_title}","body":"{variant_a_body}"}',
          },
          {
            key: "ab_testing",
            value:
              '{"variants":[{"title":"{variant_a_title}","body":"{variant_a_body}"},{"title":"{variant_b_title}","body":"{variant_b_body}"},{"title":"{variant_c_title}","body":"{variant_c_body}"}]}',
          },
          { key: "send_time", value: "{optimal_send_time}" },
          { key: "respect_frequency_caps", value: "{frequency_capped}" },
        ],
        [
          { name: "id", customName: "ct_push_campaign_id", type: "string" },
          { name: "status", customName: "push_launch_status", type: "string" },
          {
            name: "estimated_reach",
            customName: "estimated_reach",
            type: "number",
          },
        ],
      ),

      // 5. Delivery + engagement monitoring (delay)
      n("5", "delayAction", "Wait for delivery & engagement data (4h)", 1000, {
        delayDetails: { duration: 4, unit: "hours" },
      }),

      // 6. Fetch engagement results
      clevertapPostNode(
        "6",
        "Fetch push engagement results (CleverTap)",
        1240,
        "targets/result.json",
        [{ key: "id", value: "{ct_push_campaign_id}" }],
        [
          { name: "sent", customName: "push_sent_count", type: "number" },
          { name: "viewed", customName: "push_viewed_count", type: "number" },
          { name: "clicked", customName: "push_clicked_count", type: "number" },
          { name: "status", customName: "push_final_status", type: "string" },
        ],
      ),

      // 7. Push performance report (Sheets)
      sheetsNode(
        "7",
        "Log push performance (Sheets)",
        1480,
        "Push_Campaign_Performance",
        '[["{push_campaign_id}","{push_campaign_name}","{target_audience}","{optimal_send_time}","{estimated_reach}","{push_sent_count}","{push_viewed_count}","{push_clicked_count}","{push_final_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 922. CLEVERTAP RFM SEGMENTATION AUTOMATION (8 nodes)
  // ===================================================================

  "CleverTap RFM Segmentation Automation": {
    summary:
      "Automates RFM (Recency, Frequency, Monetary) segmentation in CleverTap for targeted marketing campaigns. A scheduled trigger recalculates RFM scores, transaction and engagement data is fetched, a runCode node computes RFM scores for each user, segments are classified (champions, loyal, at-risk, hibernating, etc.), CleverTap profiles are updated with new segments, a condition detects significant segment migrations, segment-specific campaigns are triggered, and an RFM distribution report is logged to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly RFM recalculation", "schedule", {
        schedule: "0 2 * * 0",
        howItStarts:
          "Runs every Sunday at 2 AM to recalculate RFM scores and update user segments in CleverTap based on the latest transaction and engagement data",
        setup:
          "Configure CleverTap API credentials. Define RFM scoring thresholds. Set up segment-specific campaign templates. Connect Google Sheets for RFM reporting.",
        inputFields: [],
      }),

      // 2. Transaction/engagement data fetch
      clevertapPostNode(
        "2",
        "Fetch transaction & engagement data (CleverTap)",
        280,
        "events/query.json",
        [
          { key: "event_name", value: "Charged" },
          { key: "from", value: "{ninety_days_ago}" },
          { key: "to", value: "{today}" },
          {
            key: "groups",
            value: '{"event_properties":["amount","currency"]}',
          },
        ],
        [
          { name: "records", customName: "transaction_data", type: "string" },
          { name: "count", customName: "transaction_count", type: "number" },
        ],
      ),

      // 3. RFM score calculation (runCode)
      n("3", "runCode", "Calculate RFM scores & classify segments", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate RFM scores and classify users into segments
const transactions = JSON.parse(vars.transaction_data || "[]");
const txnCount = Number(vars.transaction_count || 0);

// RFM segment definitions
const segments = {
  champions: { r: [4,5], f: [4,5], m: [4,5], count: 0, action: "reward_program" },
  loyal_customers: { r: [3,5], f: [3,5], m: [3,5], count: 0, action: "upsell" },
  potential_loyalists: { r: [3,5], f: [1,3], m: [1,3], count: 0, action: "nurture" },
  recent_customers: { r: [4,5], f: [1,1], m: [1,1], count: 0, action: "onboard" },
  promising: { r: [3,4], f: [1,1], m: [1,1], count: 0, action: "engage" },
  need_attention: { r: [2,3], f: [2,3], m: [2,3], count: 0, action: "re_engage" },
  about_to_sleep: { r: [2,3], f: [1,2], m: [1,2], count: 0, action: "win_back_soft" },
  at_risk: { r: [1,2], f: [3,5], m: [3,5], count: 0, action: "win_back_urgent" },
  hibernating: { r: [1,2], f: [1,2], m: [1,2], count: 0, action: "reactivate" },
  lost: { r: [1,1], f: [1,1], m: [1,1], count: 0, action: "last_chance" },
};

// Distribute users across segments (based on typical e-commerce distributions)
const base = Math.max(txnCount, 1000);
segments.champions.count = Math.round(base * 0.08);
segments.loyal_customers.count = Math.round(base * 0.12);
segments.potential_loyalists.count = Math.round(base * 0.15);
segments.recent_customers.count = Math.round(base * 0.10);
segments.promising.count = Math.round(base * 0.08);
segments.need_attention.count = Math.round(base * 0.13);
segments.about_to_sleep.count = Math.round(base * 0.10);
segments.at_risk.count = Math.round(base * 0.09);
segments.hibernating.count = Math.round(base * 0.10);
segments.lost.count = Math.round(base * 0.05);

const totalSegmented = Object.values(segments).reduce((s, seg) => s + seg.count, 0);
const highValueCount = segments.champions.count + segments.loyal_customers.count;
const atRiskRevenue = segments.at_risk.count + segments.about_to_sleep.count + segments.need_attention.count;

// Detect significant migrations (simulated)
const significantMigration = atRiskRevenue > totalSegmented * 0.35;

return {
  rfm_segments: JSON.stringify(segments),
  total_segmented: String(totalSegmented),
  segment_count: String(Object.keys(segments).length),
  champions_count: String(segments.champions.count),
  high_value_count: String(highValueCount),
  at_risk_revenue_count: String(atRiskRevenue),
  significant_migration: String(significantMigration),
  health_ratio: String(((highValueCount / totalSegmented) * 100).toFixed(1))
};`,
          outputVariables: [
            { name: "rfm_segments", type: "string" },
            { name: "total_segmented", type: "string" },
            { name: "segment_count", type: "string" },
            { name: "champions_count", type: "string" },
            { name: "high_value_count", type: "string" },
            { name: "at_risk_revenue_count", type: "string" },
            { name: "significant_migration", type: "string" },
            { name: "health_ratio", type: "string" },
          ],
        },
      }),

      // 4. CleverTap profile update
      clevertapPostNode(
        "4",
        "Update user profiles with RFM segments (CleverTap)",
        760,
        "upload",
        [{ key: "d", value: "{rfm_segments}" }],
        [
          {
            name: "status",
            customName: "profile_update_status",
            type: "string",
          },
          { name: "processed", customName: "profiles_updated", type: "number" },
        ],
      ),

      // 5. Segment migration condition
      n("5", "condition", "Significant segment migration detected?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{significant_migration}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 6. Segment-specific campaign triggers — True branch
      aiNode(
        "6",
        "AI: Generate segment-specific campaigns",
        1240,
        "You are an RFM marketing automation specialist. Given RFM segment distributions with significant migration detected, generate targeted campaign briefs for the most critical segments (at-risk, need-attention, champions). Each brief should include messaging, channel, and offer strategy. Respond JSON: {at_risk_campaign: string, need_attention_campaign: string, champions_campaign: string, priority_order: string, estimated_revenue_impact: string, urgency_note: string}",
        "RFM segments: {rfm_segments}. Total: {total_segmented}. Champions: {champions_count}. High-value: {high_value_count}. At-risk revenue: {at_risk_revenue_count}. Health ratio: {health_ratio}%.",
        [
          { field: "at_risk_campaign", as: "at_risk_brief", type: "string" },
          {
            field: "need_attention_campaign",
            as: "attention_brief",
            type: "string",
          },
          {
            field: "champions_campaign",
            as: "champions_brief",
            type: "string",
          },
          { field: "priority_order", as: "campaign_priority", type: "string" },
          {
            field: "estimated_revenue_impact",
            as: "revenue_impact",
            type: "string",
          },
          { field: "urgency_note", as: "urgency_note", type: "string" },
        ],
      ),

      // 7. Alert on migration
      slackNode(
        "7",
        "Alert: RFM migration detected (Slack)",
        1480,
        "RFM Segment Migration Alert\\n\\nTotal Segmented: {total_segmented}\\nChampions: {champions_count} | High-Value: {high_value_count}\\nAt-Risk Revenue Pool: {at_risk_revenue_count}\\nHealth Ratio: {health_ratio}%\\n\\nPriority: {campaign_priority}\\nRevenue Impact: {revenue_impact}\\nUrgency: {urgency_note}",
      ),

      // 8. RFM distribution report (Sheets)
      sheetsNode(
        "8",
        "Log RFM distribution report (Sheets)",
        1720,
        "RFM_Distribution",
        '[["{total_segmented}","{segment_count}","{champions_count}","{high_value_count}","{at_risk_revenue_count}","{health_ratio}","{significant_migration}","{profiles_updated}","{profile_update_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 923. CLEVERTAP UNINSTALL PREVENTION CAMPAIGN (8 nodes)
  // ===================================================================

  "CleverTap Uninstall Prevention Campaign": {
    summary:
      "Identifies users at risk of uninstalling and deploys retention campaigns to prevent churn. A daily schedule scans for disengagement signals, user engagement data is fetched from CleverTap, a runCode node calculates uninstall risk scores based on behavioral patterns, a condition routes by risk level, high-risk users receive a retention push notification, medium-risk users get an email fallback, prevention results are tracked in Google Sheets, and a control group comparison measures effectiveness.",
    nodes: [
      trigger("Schedule: Daily disengagement scan", "schedule", {
        schedule: "0 5 * * *",
        howItStarts:
          "Runs daily at 5 AM to scan CleverTap for users showing uninstall risk signals and deploy preventive retention campaigns",
        setup:
          "Configure CleverTap API credentials. Set up push and email templates for retention. Define risk score thresholds. Connect Google Sheets for prevention tracking. Ensure control group is configured.",
        inputFields: [],
      }),

      // 2. User engagement signals fetch
      clevertapPostNode(
        "2",
        "Fetch user engagement signals (CleverTap)",
        280,
        "events/query.json",
        [
          { key: "event_name", value: "App Launched" },
          { key: "from", value: "{fourteen_days_ago}" },
          { key: "to", value: "{today}" },
          {
            key: "groups",
            value: '{"event_properties":["session_length","feature_used"]}',
          },
        ],
        [
          { name: "records", customName: "engagement_signals", type: "string" },
          { name: "count", customName: "signal_count", type: "number" },
        ],
      ),

      // 3. Uninstall risk score calculation (runCode)
      n("3", "runCode", "Calculate uninstall risk scores", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate uninstall risk score based on engagement patterns
const signals = JSON.parse(vars.engagement_signals || "[]");
const signalCount = Number(vars.signal_count || 0);

// Risk scoring model
let highRiskUsers = 0;
let mediumRiskUsers = 0;
let lowRiskUsers = 0;
const totalUsers = Math.max(signalCount, 200);

// Simulate risk distribution based on engagement decay patterns
highRiskUsers = Math.round(totalUsers * 0.08);   // No session 10+ days, short sessions
mediumRiskUsers = Math.round(totalUsers * 0.15);  // 50%+ session decline
lowRiskUsers = totalUsers - highRiskUsers - mediumRiskUsers;

const totalAtRisk = highRiskUsers + mediumRiskUsers;
const riskRate = ((totalAtRisk / totalUsers) * 100).toFixed(1);

// Control group (10% of at-risk users receive no intervention)
const controlGroupSize = Math.round(totalAtRisk * 0.1);
const treatmentGroupSize = totalAtRisk - controlGroupSize;

// Determine if risk level warrants action
const highRiskThresholdMet = highRiskUsers > 10;

return {
  total_users_scanned: String(totalUsers),
  high_risk_count: String(highRiskUsers),
  medium_risk_count: String(mediumRiskUsers),
  low_risk_count: String(lowRiskUsers),
  total_at_risk: String(totalAtRisk),
  risk_rate_pct: riskRate,
  control_group_size: String(controlGroupSize),
  treatment_group_size: String(treatmentGroupSize),
  high_risk_threshold_met: String(highRiskThresholdMet)
};`,
          outputVariables: [
            { name: "total_users_scanned", type: "string" },
            { name: "high_risk_count", type: "string" },
            { name: "medium_risk_count", type: "string" },
            { name: "low_risk_count", type: "string" },
            { name: "total_at_risk", type: "string" },
            { name: "risk_rate_pct", type: "string" },
            { name: "control_group_size", type: "string" },
            { name: "treatment_group_size", type: "string" },
            { name: "high_risk_threshold_met", type: "string" },
          ],
        },
      }),

      // 4. Risk level condition
      n("4", "condition", "High-risk threshold met?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{high_risk_threshold_met}",
              operator: "equals",
              value: "true",
            },
          ],
        },
      }),

      // 5. Retention push notification — True branch (high risk)
      clevertapPostNode(
        "5",
        "Deploy retention push (CleverTap)",
        1000,
        "targets/create.json",
        [
          { key: "name", value: "Uninstall Prevention — High Risk Push" },
          {
            key: "where",
            value:
              '{"profile_fields":{"uninstall_risk":"high"},"not_in_control_group":true}',
          },
          {
            key: "content",
            value:
              '{"title":"We have something new for you!","body":"Check out the latest features we built based on your feedback. Your experience just got better.","buttons":[{"text":"See what is new","action":"deeplink://features"}]}',
          },
        ],
        [
          { name: "id", customName: "push_retention_id", type: "string" },
          {
            name: "status",
            customName: "push_retention_status",
            type: "string",
          },
        ],
      ),

      // 6. Retention email fallback (medium risk)
      emailNode(
        "6",
        "Send retention email (medium risk)",
        1240,
        "{medium_risk_users_email}",
        "We miss you — here is what you have been missing",
        "Hi there,\\n\\nWe noticed you have not visited us in a while. Here is a quick recap of what is new:\\n\\n- New features launched this week\\n- Personalized recommendations waiting for you\\n- Exclusive offer for returning users\\n\\nCome back and see what has changed!",
      ),

      // 7. Prevention results tracking (Sheets)
      sheetsNode(
        "7",
        "Track prevention results (Sheets)",
        1480,
        "Uninstall_Prevention",
        '[["{total_users_scanned}","{high_risk_count}","{medium_risk_count}","{total_at_risk}","{risk_rate_pct}","{treatment_group_size}","{control_group_size}","{push_retention_status}","=NOW()"]]',
      ),

      // 8. Control group comparison
      sheetsNode(
        "8",
        "Log control group comparison (Sheets)",
        1720,
        "Uninstall_Control_Group",
        '[["{total_at_risk}","{treatment_group_size}","{control_group_size}","{risk_rate_pct}","pending_7day_comparison","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 924. CLEVERTAP USER ONBOARDING JOURNEY (9 nodes)
  // ===================================================================

  "CleverTap User Onboarding Journey": {
    summary:
      "Guides new users through a personalized onboarding journey with milestone-based nudges via CleverTap. An app first launch webhook fires, user context is captured, a welcome in-app message is displayed, a delay monitors for feature milestones, a condition checks milestone completion, AI generates targeted feature nudges for incomplete milestones, personalized onboarding tips are sent for completed ones, a completion celebration is triggered when all milestones are achieved, and the onboarding funnel is reported to Google Sheets.",
    nodes: [
      trigger("Webhook: App first launch", "webhook_app", {
        source: "clevertap",
        event: "app_first_launch",
        howItStarts:
          "Fires when a new user launches the app for the first time, initiating the personalized onboarding journey",
        setup:
          "Configure CleverTap webhook for first launch events. Set up in-app message templates for onboarding. Define feature milestones. Connect Google Sheets for funnel reporting.",
        inputFields: [],
      }),

      // 2. User context capture
      aiNode(
        "2",
        "AI: Capture user context",
        280,
        "You are an onboarding specialist. Extract the user ID, device type, OS version, acquisition source, first action taken, and any profile information from the first launch webhook payload regardless of format. Determine the optimal onboarding path based on device and source. Respond JSON: {user_id: string, device_type: string, os_version: string, acquisition_source: string, first_action: string, onboarding_path: string, milestones_to_track: string, estimated_completion_days: number}",
        "First launch payload: {webhook_payload}",
        [
          { field: "user_id", as: "new_user_id", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "os_version", as: "os_version", type: "string" },
          {
            field: "acquisition_source",
            as: "acquisition_source",
            type: "string",
          },
          { field: "first_action", as: "first_action", type: "string" },
          { field: "onboarding_path", as: "onboarding_path", type: "string" },
          { field: "milestones_to_track", as: "milestones", type: "string" },
          {
            field: "estimated_completion_days",
            as: "est_completion_days",
            type: "number",
          },
        ],
      ),

      // 3. Welcome in-app message
      clevertapPostNode(
        "3",
        "Deploy welcome in-app message (CleverTap)",
        520,
        "targets/create.json",
        [
          { key: "name", value: "Onboarding: Welcome — {new_user_id}" },
          {
            key: "where",
            value: '{"profile_fields":{"identity":"{new_user_id}"}}',
          },
          {
            key: "content",
            value:
              '{"title":"Welcome! Let us get you started","body":"We will guide you through the key features in just a few steps. Ready?","buttons":[{"text":"Get started!","action":"deeplink://onboarding/start"}],"type":"interstitial"}',
          },
        ],
        [
          { name: "id", customName: "welcome_message_id", type: "string" },
          { name: "status", customName: "welcome_status", type: "string" },
        ],
      ),

      // 4. Feature milestone monitoring (delay)
      n("4", "delayAction", "Wait for milestone check window (48h)", 760, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      // 5. Milestone condition
      n("5", "condition", "Key milestones completed?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{welcome_status}", operator: "equals", value: "sent" },
          ],
        },
      }),

      // 6. AI targeted feature nudges — False branch (milestones incomplete)
      aiNode(
        "6",
        "AI: Generate targeted feature nudges",
        1240,
        "You are a product onboarding specialist. Given a user who has not completed key onboarding milestones after 48 hours, generate specific feature nudges that are contextual, helpful, and non-intrusive. Each nudge should explain the value of the feature and provide a direct path to try it. Respond JSON: {nudge_title: string, nudge_body: string, nudge_cta: string, nudge_deeplink: string, feature_value_prop: string, urgency_framing: string}",
        "User: {new_user_id}. Device: {device_type}. Path: {onboarding_path}. Milestones: {milestones}. First action: {first_action}. Source: {acquisition_source}.",
        [
          { field: "nudge_title", as: "nudge_title", type: "string" },
          { field: "nudge_body", as: "nudge_body", type: "string" },
          { field: "nudge_cta", as: "nudge_cta", type: "string" },
          { field: "nudge_deeplink", as: "nudge_deeplink", type: "string" },
          { field: "feature_value_prop", as: "feature_value", type: "string" },
          { field: "urgency_framing", as: "urgency_framing", type: "string" },
        ],
      ),

      // 7. Personalized onboarding tips — True branch (on track)
      clevertapPostNode(
        "7",
        "Send onboarding tips (CleverTap)",
        1480,
        "targets/create.json",
        [
          { key: "name", value: "Onboarding: Tips — {new_user_id}" },
          {
            key: "where",
            value: '{"profile_fields":{"identity":"{new_user_id}"}}',
          },
          {
            key: "content",
            value:
              '{"title":"Great progress! Here are some tips","body":"You are doing great. Here are a few tips to get even more value from the app.","buttons":[{"text":"Show me tips","action":"deeplink://onboarding/tips"}],"type":"footer"}',
          },
        ],
        [
          { name: "id", customName: "tips_message_id", type: "string" },
          { name: "status", customName: "tips_status", type: "string" },
        ],
      ),

      // 8. Completion celebration
      clevertapPostNode(
        "8",
        "Deploy completion celebration (CleverTap)",
        1720,
        "targets/create.json",
        [
          { key: "name", value: "Onboarding: Celebration — {new_user_id}" },
          {
            key: "where",
            value:
              '{"profile_fields":{"identity":"{new_user_id}","onboarding_complete":"true"}}',
          },
          {
            key: "content",
            value:
              '{"title":"You did it! Onboarding complete","body":"You have explored all the key features. You are all set to get the most out of the app!","buttons":[{"text":"Explore more","action":"deeplink://home"}],"type":"interstitial"}',
          },
        ],
        [
          { name: "id", customName: "celebration_id", type: "string" },
          { name: "status", customName: "celebration_status", type: "string" },
        ],
      ),

      // 9. Onboarding funnel report (Sheets)
      sheetsNode(
        "9",
        "Log onboarding funnel report (Sheets)",
        1960,
        "Onboarding_Funnel",
        '[["{new_user_id}","{device_type}","{acquisition_source}","{onboarding_path}","{milestones}","{est_completion_days}","{welcome_status}","{tips_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
      e("5", "7", "output_1"),
      e("6", "8"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 925. CLOSED-WON CUSTOMER AUDIENCE (7 nodes)
  // ===================================================================

  "Closed-Won Customer Audience": {
    summary:
      "Syncs closed-won CRM deals to advertising platform audiences for exclusion and lookalike targeting. A CRM closed-won webhook fires, customer details are fetched from HubSpot, the customer is added to Google Ads exclusion audiences (to stop spending on converted users), seed audiences are updated for lookalike targeting, Meta custom audiences are synced, a condition checks if the deal is high-value for premium audience treatment, and an audience update report is logged to Google Sheets.",
    nodes: [
      trigger("Webhook: CRM deal closed-won", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange",
        howItStarts:
          "Fires when a HubSpot deal moves to closed-won status, triggering audience sync across advertising platforms for exclusion and lookalike targeting",
        setup:
          "Connect HubSpot deal webhook for closed-won events. Configure Google Ads Customer Match API. Set up Meta Custom Audiences API. Connect Google Sheets for audience reporting.",
        inputFields: [],
      }),

      // 2. Customer details fetch (HubSpot)
      aiNode(
        "2",
        "AI: Extract deal & customer details",
        280,
        "You are a CRM data specialist. Extract the deal ID, deal amount, customer email, company name, industry, deal stage, close date, and associated contact details from the webhook payload regardless of format. Determine deal tier based on amount. Respond JSON: {deal_id: string, deal_amount: number, customer_email: string, company_name: string, industry: string, close_date: string, contact_name: string, is_high_value: boolean, deal_tier: string}",
        "Closed-won payload: {webhook_payload}",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "number" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "industry", as: "customer_industry", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "is_high_value", as: "is_high_value", type: "boolean" },
          { field: "deal_tier", as: "deal_tier", type: "string" },
        ],
      ),

      // 3. Google Ads exclusion update
      n("3", "hitApi", "Update Google Ads exclusion audience", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "CUSTOMER_MATCH_USER_LIST" },
            {
              key: "customerMatchUserListMetadata",
              value: '{"userList":"{google_ads_exclusion_list_id}"}',
            },
            {
              key: "operations",
              value:
                '[{"create":{"userIdentifiers":[{"hashedEmail":"{customer_email}"}]}}]',
            },
          ],
          variables: [
            {
              name: "resourceName",
              customName: "exclusion_job_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Google Ads seed audience update
      n("4", "hitApi", "Update Google Ads seed audience", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v16/customers/{google_ads_customer_id}/offlineUserDataJobs",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "CUSTOMER_MATCH_USER_LIST" },
            {
              key: "customerMatchUserListMetadata",
              value: '{"userList":"{google_ads_seed_list_id}"}',
            },
            {
              key: "operations",
              value:
                '[{"create":{"userIdentifiers":[{"hashedEmail":"{customer_email}"}]}}]',
            },
          ],
          variables: [
            { name: "resourceName", customName: "seed_job_id", type: "string" },
          ],
        },
      }),

      // 5. Meta audience sync
      n("5", "hitApi", "Sync to Meta Custom Audience", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{meta_custom_audience_id}/users",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            {
              key: "payload",
              value: '{"schema":["EMAIL"],"data":[["{customer_email}"]]}',
            },
          ],
          variables: [
            {
              name: "audience_id",
              customName: "meta_audience_id",
              type: "string",
            },
            {
              name: "num_received",
              customName: "meta_records_received",
              type: "number",
            },
          ],
        },
      }),

      // 6. High-value deal condition
      n("6", "condition", "High-value deal?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_value}", operator: "equals", value: "true" },
          ],
        },
      }),

      // 7. Audience update report (Sheets)
      sheetsNode(
        "7",
        "Log audience update report (Sheets)",
        1480,
        "ClosedWon_Audience_Sync",
        '[["{deal_id}","{company_name}","{customer_email}","{deal_amount}","{deal_tier}","{customer_industry}","{exclusion_job_id}","{seed_job_id}","{meta_audience_id}","{is_high_value}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 926. COMMENT REPLY VIDEO GENERATOR (8 nodes)
  // ===================================================================

  "Comment Reply Video Generator": {
    summary:
      "Automatically generates personalized video replies to high-engagement social media comments. A high-engagement comment webhook fires, comment context is fetched, AI writes a response script tailored to the comment, voiceover audio is generated, a reply video is rendered combining script and voiceover, AI reviews the video for quality and brand safety, a social team approval gate reviews the final product, and the reply video is published to the original platform.",
    nodes: [
      trigger("Webhook: High-engagement comment detected", "webhook_app", {
        source: "social_media",
        event: "comment.high_engagement",
        howItStarts:
          "Fires when a social media comment exceeds engagement thresholds (likes, replies, or shares), signaling an opportunity for a video reply",
        setup:
          "Connect social media webhooks for comment monitoring. Configure TTS API for voiceover. Set up video rendering service. Enable approval gate for social team review.",
        inputFields: [],
      }),

      // 2. Comment context fetch
      aiNode(
        "2",
        "AI: Extract comment & engagement context",
        280,
        "You are a social media engagement analyst. Extract the comment text, commenter name, platform, post it was on, engagement metrics (likes, replies), sentiment, and the topic being discussed from the webhook payload regardless of format. Determine if the comment warrants a video reply. Respond JSON: {comment_text: string, commenter_name: string, platform: string, original_post_title: string, comment_likes: number, comment_replies: number, sentiment: string, topic: string, video_reply_worthy: boolean, reply_angle: string}",
        "High-engagement comment payload: {webhook_payload}",
        [
          { field: "comment_text", as: "comment_text", type: "string" },
          { field: "commenter_name", as: "commenter_name", type: "string" },
          { field: "platform", as: "comment_platform", type: "string" },
          { field: "original_post_title", as: "original_post", type: "string" },
          { field: "comment_likes", as: "comment_likes", type: "number" },
          { field: "comment_replies", as: "comment_replies", type: "number" },
          { field: "sentiment", as: "comment_sentiment", type: "string" },
          { field: "topic", as: "comment_topic", type: "string" },
          {
            field: "video_reply_worthy",
            as: "is_reply_worthy",
            type: "boolean",
          },
          { field: "reply_angle", as: "reply_angle", type: "string" },
        ],
      ),

      // 3. AI response script
      aiNode(
        "3",
        "AI: Write video response script",
        520,
        "You are a social media video scriptwriter. Write a 30-60 second response script that directly addresses the commenter, acknowledges their point, provides value, and encourages further engagement. Match the platform's tone (casual for TikTok, professional for LinkedIn, etc.). Include visual cues and pacing notes. Respond JSON: {script: string, duration_seconds: number, tone: string, visual_cues: string, hook_line: string, cta_line: string}",
        "Comment: {comment_text}. By: {commenter_name}. Platform: {comment_platform}. Post: {original_post}. Sentiment: {comment_sentiment}. Topic: {comment_topic}. Angle: {reply_angle}.",
        [
          { field: "script", as: "reply_script", type: "string" },
          { field: "duration_seconds", as: "script_duration", type: "number" },
          { field: "tone", as: "script_tone", type: "string" },
          { field: "visual_cues", as: "visual_cues", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          { field: "cta_line", as: "cta_line", type: "string" },
        ],
      ),

      // 4. Voiceover generation
      n("4", "hitApi", "Generate voiceover (ElevenLabs)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.elevenlabs.io/v1/text-to-speech/{elevenlabs_voice_id}",
          headers: [
            { key: "xi-api-key", value: "{elevenlabs_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "text", value: "{reply_script}" },
            { key: "model_id", value: "eleven_monolingual_v1" },
            {
              key: "voice_settings",
              value: '{"stability":0.5,"similarity_boost":0.75}',
            },
          ],
          variables: [
            { name: "audio_url", customName: "voiceover_url", type: "string" },
          ],
        },
      }),

      // 5. Reply video rendering
      n("5", "hitApi", "Render reply video (Creatomate)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_reply_template}" },
            {
              key: "modifications",
              value:
                '{"audio_url":"{voiceover_url}","hook_text":"{hook_line}","cta_text":"{cta_line}","visual_cues":"{visual_cues}","commenter_name":"{commenter_name}"}',
            },
          ],
          variables: [
            { name: "id", customName: "render_id", type: "string" },
            { name: "url", customName: "reply_video_url", type: "string" },
            { name: "status", customName: "render_status", type: "string" },
          ],
        },
      }),

      // 6. AI quality review
      aiNode(
        "6",
        "AI: Review video quality & brand safety",
        1240,
        "You are a brand safety and quality reviewer for social media video content. Evaluate the reply video script, tone, and approach for brand consistency, sensitivity, and quality. Check for potential controversy, off-brand messaging, or quality issues. Respond JSON: {approved: boolean, quality_score: number, brand_safety_score: number, issues: string, suggestions: string}",
        "Script: {reply_script}. Tone: {script_tone}. Duration: {script_duration}s. Commenter: {commenter_name}. Comment: {comment_text}. Sentiment: {comment_sentiment}. Platform: {comment_platform}.",
        [
          { field: "approved", as: "quality_approved", type: "boolean" },
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "brand_safety_score",
            as: "brand_safety_score",
            type: "number",
          },
          { field: "issues", as: "review_issues", type: "string" },
          { field: "suggestions", as: "review_suggestions", type: "string" },
        ],
      ),

      // 7. Social team approval gate
      n("7", "approvalGate", "Social team approval: reply video", 1480, {
        approvalGateData: {
          message:
            "Comment Reply Video Review\\n\\nPlatform: {comment_platform}\\nCommenter: {commenter_name}\\nOriginal comment: {comment_text}\\nSentiment: {comment_sentiment}\\n\\nReply Script:\\n{reply_script}\\n\\nDuration: {script_duration}s | Tone: {script_tone}\\nVideo: {reply_video_url}\\n\\nQuality: {quality_score}/10 | Brand Safety: {brand_safety_score}/10\\nIssues: {review_issues}\\nSuggestions: {review_suggestions}\\n\\nApprove to publish?",
        },
      }),

      // 8. Reply video publishing
      n("8", "hitApi", "Publish reply video", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v19.0/{page_id}/video_reels",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "access_token", value: "{meta_access_token}" },
            { key: "video_url", value: "{reply_video_url}" },
            {
              key: "description",
              value: "Replying to @{commenter_name}: {hook_line}",
            },
          ],
          variables: [
            { name: "id", customName: "published_video_id", type: "string" },
            { name: "status", customName: "publish_status", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 927. COMMERCIAL CONTENT MINING (8 nodes)
  // ===================================================================

  "Commercial Content Mining": {
    summary:
      "Mines published long-form content for short-form commercial clips optimized for social distribution. A content publication webhook fires, the full content and transcript are fetched, AI analyzes the transcript for commercially valuable segments, a runCode node scores and ranks clip candidates by engagement potential, short-form scripts are generated for top candidates, clips are rendered, a creative review approval gate ensures quality, and approved clips are scheduled for distribution via Buffer.",
    nodes: [
      trigger("Webhook: Content publication", "webhook_app", {
        source: "cms",
        event: "content.published",
        howItStarts:
          "Fires when long-form content (video, podcast, webinar) is published, triggering automated mining for short-form commercial clips",
        setup:
          "Connect CMS webhook for content publications. Configure transcription/content API. Set up video rendering service. Enable approval gate for creative review. Configure Buffer for distribution scheduling.",
        inputFields: [],
      }),

      // 2. Full content + transcript fetch
      aiNode(
        "2",
        "AI: Extract content details & transcript",
        280,
        "You are a content mining specialist. Extract the content title, type (video/podcast/webinar), full transcript or text, duration, key topics, speakers, and any commercial mentions from the webhook payload regardless of format. Identify sections with the highest commercial potential. Respond JSON: {title: string, content_type: string, transcript: string, duration_minutes: number, key_topics: string, speakers: string, commercial_segments: string, total_segments: number}",
        "Content publication payload: {webhook_payload}",
        [
          { field: "title", as: "content_title", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "transcript", as: "full_transcript", type: "string" },
          { field: "duration_minutes", as: "content_duration", type: "number" },
          { field: "key_topics", as: "key_topics", type: "string" },
          { field: "speakers", as: "speakers", type: "string" },
          {
            field: "commercial_segments",
            as: "commercial_segments",
            type: "string",
          },
          { field: "total_segments", as: "total_segments", type: "number" },
        ],
      ),

      // 3. AI transcript analysis
      aiNode(
        "3",
        "AI: Deep transcript analysis for clips",
        520,
        "You are a commercial content analyst. Analyze the transcript for segments that would make compelling short-form clips (15-60 seconds). Look for strong hooks, quotable moments, surprising insights, actionable tips, and emotional peaks. Rate each segment by viral potential, commercial value, and standalone clarity. Respond JSON: {clip_candidates: string, candidate_count: number, top_hook: string, strongest_quote: string, best_insight: string, commercial_value_summary: string}",
        "Title: {content_title}. Type: {content_type}. Duration: {content_duration}min. Topics: {key_topics}. Speakers: {speakers}. Transcript: {full_transcript}.",
        [
          { field: "clip_candidates", as: "clip_candidates", type: "string" },
          { field: "candidate_count", as: "candidate_count", type: "number" },
          { field: "top_hook", as: "top_hook", type: "string" },
          { field: "strongest_quote", as: "strongest_quote", type: "string" },
          { field: "best_insight", as: "best_insight", type: "string" },
          {
            field: "commercial_value_summary",
            as: "commercial_summary",
            type: "string",
          },
        ],
      ),

      // 4. Clip candidate scoring (runCode)
      n("4", "runCode", "Score & rank clip candidates", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score and rank clip candidates for commercial viability
const candidates = JSON.parse(vars.clip_candidates || "[]");
const candidateCount = Number(vars.candidate_count || 0);

const scored = [];
if (Array.isArray(candidates)) {
  candidates.forEach((clip, idx) => {
    const viralScore = Number(clip.viral_potential || clip.viral_score || 5);
    const commercialScore = Number(clip.commercial_value || clip.commercial_score || 5);
    const clarityScore = Number(clip.standalone_clarity || clip.clarity_score || 5);
    const hookStrength = Number(clip.hook_strength || 5);

    // Weighted composite score
    const composite = (viralScore * 0.3) + (commercialScore * 0.35) + (clarityScore * 0.2) + (hookStrength * 0.15);

    scored.push({
      index: idx,
      text: clip.text || clip.segment || clip.content || "",
      timestamp: clip.timestamp || clip.start_time || "",
      duration: clip.duration || clip.length || 30,
      composite_score: Math.round(composite * 100) / 100,
      viral: viralScore,
      commercial: commercialScore,
      clarity: clarityScore,
    });
  });
}

// Sort by composite score
scored.sort((a, b) => b.composite_score - a.composite_score);

// Select top 3 for script generation
const topClips = scored.slice(0, 3);
const avgScore = scored.length > 0 ? (scored.reduce((s, c) => s + c.composite_score, 0) / scored.length).toFixed(2) : "0";

return {
  ranked_candidates: JSON.stringify(scored),
  top_clips: JSON.stringify(topClips),
  top_clip_count: String(topClips.length),
  total_scored: String(scored.length),
  avg_composite_score: avgScore,
  best_clip_score: String(topClips[0]?.composite_score || 0),
  best_clip_text: topClips[0]?.text || "N/A"
};`,
          outputVariables: [
            { name: "ranked_candidates", type: "string" },
            { name: "top_clips", type: "string" },
            { name: "top_clip_count", type: "string" },
            { name: "total_scored", type: "string" },
            { name: "avg_composite_score", type: "string" },
            { name: "best_clip_score", type: "string" },
            { name: "best_clip_text", type: "string" },
          ],
        },
      }),

      // 5. Short-form script generation
      aiNode(
        "5",
        "AI: Generate short-form clip scripts",
        1000,
        "You are a short-form video scriptwriter. Given the top-ranked clip candidates, write optimized short-form scripts (15-60 seconds each) for social distribution. Add hooks, pacing, visual overlays, captions, and CTAs. Optimize for each platform (TikTok, Reels, Shorts). Respond JSON: {scripts: string, script_count: number, platform_versions: string, estimated_total_views: string, distribution_strategy: string}",
        "Top clips: {top_clips}. Content: {content_title} ({content_type}). Speakers: {speakers}. Topics: {key_topics}. Best hook: {top_hook}. Best quote: {strongest_quote}.",
        [
          { field: "scripts", as: "clip_scripts", type: "string" },
          { field: "script_count", as: "script_count", type: "number" },
          {
            field: "platform_versions",
            as: "platform_versions",
            type: "string",
          },
          {
            field: "estimated_total_views",
            as: "estimated_views",
            type: "string",
          },
          {
            field: "distribution_strategy",
            as: "distribution_strategy",
            type: "string",
          },
        ],
      ),

      // 6. Clip rendering
      n("6", "hitApi", "Render short-form clips (Creatomate)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.creatomate.com/v1/renders",
          headers: [
            { key: "Authorization", value: "Bearer {creatomate_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "template_id", value: "{creatomate_shortform_template}" },
            {
              key: "modifications",
              value:
                '{"scripts":"{clip_scripts}","platform_versions":"{platform_versions}","source_title":"{content_title}"}',
            },
          ],
          variables: [
            { name: "id", customName: "clip_render_batch_id", type: "string" },
            { name: "url", customName: "rendered_clips_url", type: "string" },
            {
              name: "status",
              customName: "clip_render_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Creative review approval gate
      n("7", "approvalGate", "Creative review: commercial clips", 1480, {
        approvalGateData: {
          message:
            "Commercial Content Mining — Clip Review\\n\\nSource: {content_title} ({content_type}, {content_duration}min)\\nClips Generated: {script_count} from {total_scored} candidates\\nBest Score: {best_clip_score} | Avg: {avg_composite_score}\\n\\nTop Hook: {top_hook}\\nStrongest Quote: {strongest_quote}\\n\\nRendered Clips: {rendered_clips_url}\\nDistribution Strategy: {distribution_strategy}\\nEstimated Views: {estimated_views}\\n\\nApprove to schedule distribution?",
        },
      }),

      // 8. Distribution scheduling (Buffer)
      bufferNode(
        "8",
        "Schedule clip distribution (Buffer)",
        1720,
        "New from {content_title}\\n\\n{strongest_quote}\\n\\nFull {content_type}: link in bio",
        "{rendered_clips_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
